import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar, type SidebarMenuItem } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  patientsService,
  PatientWithAppointments,
  PatientMealPlan,
  MealPlanPreferences,
} from '../../services/patients/patientsService';
import { apiClient } from '../../services/api/apiClient';
import PatientNutritionProfileTab from './PatientNutritionProfileTab';
import PatientMealPlanTab from './PatientMealPlanTab';
import { nutritionProfileService, CurrentNutritionState, derivedMetricsService, DerivedMetricsSummaryResponse, DerivedMetricSummaryItem } from '../../services/nutrition/nutritionProfileService';
import { VITAL_BACKED_DERIVED_METRICS } from '../../services/nutrition/derivedMetricsVitals';
import {
  dashboardService,
  AppointmentWithDetails,
  AvailableSlotsResponse,
} from '../../services/dashboard/dashboardService';
import { videoConferenceService, CanJoinCallResponse } from '../../services/video-conference/videoConferenceService';
import { getCurrentDoctor } from '../../services/doctor-onboarding/doctorOnboardingService';
import { profileService, DoctorPractice } from '../../services/profile/profileService';
import { leaveSlotService, LeaveSlot } from '../../services/availability/leaveSlotService';
import AvailabilityCalendar from '../../components/availability/AvailabilityCalendar/AvailabilityCalendar';
import { getConsultationTypeLabel } from '../../utils/appointmentUtils';
import { formatDate, formatDateTime, formatAppointmentTime } from '../../utils/dateUtils';
import { getAppointmentStatusBadgeClass, getAppointmentStatusLabel } from '../../utils/statusUtils';
import { getInitials } from '../../utils/userUtils';
import { getChatStatusWebSocketUrl } from '../../../../shared/utils/constants';
import { HealthVitalsInsightsView } from '../../components/health/HealthVitalsInsightsView';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import './PatientDetailPage.css';

// Feature flag: Enable WebSocket for real-time updates (optional, long-polling is primary)
const USE_CHAT_WEBSOCKET = process.env.REACT_APP_USE_CHAT_WEBSOCKET === 'true' || false;

const PatientDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams<{ patientId: string }>();
  
  // Helper functions for localStorage persistence (defined BEFORE useState to avoid initialization errors)
  const getChatSessionStorageKey = (patientId: string) => `mealPlanChatSession_${patientId}`;
  const getActiveTabStorageKey = (patientId: string) => `patientActiveTab_${patientId}`;
  
  const saveChatSessionId = (patientId: string, sessionId: number | null) => {
    if (sessionId) {
      localStorage.setItem(getChatSessionStorageKey(patientId), sessionId.toString());
    } else {
      localStorage.removeItem(getChatSessionStorageKey(patientId));
    }
  };
  
  const loadChatSessionId = (patientId: string): number | null => {
    const stored = localStorage.getItem(getChatSessionStorageKey(patientId));
    return stored ? Number(stored) : null;
  };
  
  const saveActiveTab = (patientId: string, tab: 'appointments' | 'mealPlan' | 'nutritionProfile' | 'metrics' | 'scores') => {
    localStorage.setItem(getActiveTabStorageKey(patientId), tab);
    // Also update URL
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  };
  
  const loadActiveTab = (patientId: string): 'appointments' | 'mealPlan' | 'nutritionProfile' | 'metrics' | 'scores' => {
    // First check URL
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'mealPlan' || tabParam === 'nutritionProfile' || tabParam === 'appointments' || tabParam === 'metrics' || tabParam === 'scores') {
      return tabParam;
    }
    // Then check localStorage
    const stored = localStorage.getItem(getActiveTabStorageKey(patientId));
    if (stored === 'mealPlan' || stored === 'nutritionProfile' || stored === 'appointments' || stored === 'metrics' || stored === 'scores') {
      return stored;
    }
    return 'appointments'; // Default
  };
  
  const [patient, setPatient] = useState<PatientWithAppointments | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chatWsRef = useRef<WebSocket | null>(null);
  const [chatWsConnected, setChatWsConnected] = useState(false);
  
  // Initialize active tab from URL or localStorage
  const [activeTab, setActiveTabState] = useState<'appointments' | 'mealPlan' | 'nutritionProfile' | 'metrics' | 'scores'>(() => {
    if (patientId) {
      return loadActiveTab(patientId);
    }
    return 'appointments';
  });
  
  // Wrapper to persist tab changes
  const setActiveTab = (tab: 'appointments' | 'mealPlan' | 'nutritionProfile' | 'metrics' | 'scores') => {
    setActiveTabState(tab);
    if (patientId) {
      saveActiveTab(patientId, tab);
    }
  };

  // Sub-tab state for Insights (Metrics vs Scores)
  const [insightsSubTab, setInsightsSubTab] = useState<'metrics' | 'scores'>('metrics');
  
  const [allMealPlans, setAllMealPlans] = useState<PatientMealPlan[]>([]);
  const [mealPlanLoading, setMealPlanLoading] = useState(false);
  const [mealPlanError, setMealPlanError] = useState<string | null>(null);
  const [isMealPlanEditMode, setIsMealPlanEditMode] = useState(false);
  const [currentDoctorId, setCurrentDoctorId] = useState<number | null>(null);
  const [deletingGoalId, setDeletingGoalId] = useState<number | null>(null);
  const [showNutritionProfileWarning, setShowNutritionProfileWarning] = useState(false);
  const [derivedState, setDerivedState] = useState<CurrentNutritionState | null>(null);
  const [derivedLoading, setDerivedLoading] = useState(false);
  const [derivedError, setDerivedError] = useState<string | null>(null);
  const [derivedMetricsSummary, setDerivedMetricsSummary] = useState<DerivedMetricsSummaryResponse | null>(null);
  const [derivedMetricsLoading, setDerivedMetricsLoading] = useState(false);
  const [derivedMetricsError, setDerivedMetricsError] = useState<string | null>(null);
  const [insightsEdits, setInsightsEdits] = useState<Record<string, number>>({});
  const [insightsSaving, setInsightsSaving] = useState(false);
  const [insightsMessage, setInsightsMessage] = useState<string | null>(null);
  const [insightsMessageLevel, setInsightsMessageLevel] = useState<'info' | 'success' | 'error'>('info');
  const [insightsEditMode, setInsightsEditMode] = useState(false);

  const filteredDerivedInsights = useMemo(() => {
    if (!derivedMetricsSummary?.items) return [];
    return derivedMetricsSummary.items
      .filter((item) => {
        if (insightsSubTab === 'metrics') {
          return item.category !== 'Composite Score' && !item.category?.toLowerCase().includes('composite');
        }
        return item.category === 'Composite Score' || item.category?.toLowerCase().includes('composite');
      })
      .sort((a, b) => a.metric_key.localeCompare(b.metric_key));
  }, [derivedMetricsSummary, insightsSubTab]);

  const isCompositeScore = (item: DerivedMetricSummaryItem) =>
    item.category === 'Composite Score' || (item.category?.toLowerCase() ?? '').includes('composite');

  // Only include metrics the user has actually edited (or that came from document). Do not re-send backend-only values.
  const editableInsightsForVitals = useMemo(
    () =>
      filteredDerivedInsights
        .filter((item) => !isCompositeScore(item))
        .filter((item) => insightsEdits[item.metric_key] !== undefined)
        .map((item) => {
          const editValue = insightsEdits[item.metric_key];
          const valueToSave = typeof editValue === 'number' && Number.isFinite(editValue) ? editValue : null;
          const vitalType = VITAL_BACKED_DERIVED_METRICS[item.metric_key];
          if (vitalType === undefined || valueToSave == null) {
            return null;
          }
          return {
            item,
            editValue: valueToSave,
            vitalType,
          };
        })
        .filter(
          (entry): entry is { item: DerivedMetricSummaryItem; editValue: number; vitalType: string } => entry !== null
        ),
    [filteredDerivedInsights, insightsEdits]
  );

  const editableInsightsForScores = useMemo(
    () =>
      filteredDerivedInsights
        .filter((item) => isCompositeScore(item))
        .filter((item) => insightsEdits[item.metric_key] !== undefined)
        .map((item) => {
          const editValue = insightsEdits[item.metric_key];
          const valueToSave = typeof editValue === 'number' && Number.isFinite(editValue) ? editValue : null;
          if (valueToSave == null) return null;
          return { item, valueToSave };
        })
        .filter(
          (entry): entry is { item: DerivedMetricSummaryItem; valueToSave: number } => entry !== null
        ),
    [filteredDerivedInsights, insightsEdits]
  );

  const editableInsightsCount = editableInsightsForVitals.length + editableInsightsForScores.length;

  const handleInsightsSave = useCallback(async () => {
    if (editableInsightsCount === 0) {
      setInsightsMessage('Select at least one editable metric before saving.');
      setInsightsMessageLevel('info');
      return;
    }
    if (!patientId) {
      setInsightsMessage('Missing patient identifier.');
      setInsightsMessageLevel('error');
      return;
    }
    const numericPatientId = Number(patientId);
    if (Number.isNaN(numericPatientId)) {
      setInsightsMessage('Invalid patient identifier.');
      setInsightsMessageLevel('error');
      return;
    }

    setInsightsSaving(true);
    setInsightsMessage(null);
    try {
      const recordedAt = new Date().toISOString();
      let message = '';
      if (editableInsightsForVitals.length > 0) {
        const payloadEntries = editableInsightsForVitals.map(({ item, editValue, vitalType }) => ({
          metric_type: vitalType,
          value: editValue,
          unit: item.unit || item.reference_unit || '',
          recorded_at: recordedAt,
          notes: `Doctor insights edit (${item.display_name})`,
          source_device: 'Doctor Portal Insights',
        }));
        const response = await derivedMetricsService.saveInsightsVitals(numericPatientId, {
          entries: payloadEntries,
        });
        message = response.message || 'Vitals saved and aggregation triggered.';
      }
      if (editableInsightsForScores.length > 0) {
        const scoreEntries = editableInsightsForScores.map(({ item, valueToSave }) => ({
          score_name: item.metric_key,
          value: valueToSave,
          recorded_at: recordedAt,
        }));
        const scoreResponse = await derivedMetricsService.saveInsightsDerivedScores(numericPatientId, {
          entries: scoreEntries,
        });
        const scoreMsg = scoreResponse.processed_count
          ? `${scoreResponse.processed_count} composite score(s) saved.`
          : scoreResponse.message;
        message = message ? `${message} ${scoreMsg}` : scoreMsg;
      }
      setInsightsMessageLevel('success');
      setInsightsMessage(message || 'Saved.');
      setInsightsEditMode(true);
      setInsightsEdits((prev) => {
        const next = { ...prev };
        editableInsightsForVitals.forEach(({ item }) => {
          delete next[item.metric_key];
        });
        editableInsightsForScores.forEach(({ item }) => {
          delete next[item.metric_key];
        });
        return next;
      });

      try {
        const summary = await derivedMetricsService.getSummary(numericPatientId);
        setDerivedMetricsSummary(summary);
      } catch {
        // Summary will refresh on the next load
      }
    } catch (saveError: any) {
      const rawDetail = saveError?.response?.data?.detail;
      const detail =
        (typeof rawDetail === 'string'
          ? rawDetail
          : Array.isArray(rawDetail)
            ? rawDetail.map((item) => item?.msg || JSON.stringify(item)).join(', ')
            : rawDetail
              ? JSON.stringify(rawDetail)
              : undefined) ||
        saveError?.message ||
        'Failed to save vitals for the selected metrics.';
      setInsightsMessage(detail);
      setInsightsMessageLevel('error');
    } finally {
      setInsightsSaving(false);
    }
  }, [editableInsightsCount, editableInsightsForVitals, editableInsightsForScores, patientId]);

  const handleCancelInsightsEdit = useCallback(() => {
    setInsightsEdits({});
    setInsightsEditMode(false);
    setInsightsMessage(null);
  }, []);

  const [requirementsChatSessionId, setRequirementsChatSessionId] = useState<number | null>(null);
  const [showRequirementsChat, setShowRequirementsChat] = useState(false); // Track if chat should be shown (even without session ID)
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatMessageInput, setChatMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [, setLastSeenMessageId] = useState<number>(0);
  const lastSeenMessageIdRef = useRef<number>(0);
  const [, setCurrentStatus] = useState<{ status: string; progress?: number; agentName?: string } | null>(null);
  const chatPollingRef = useRef<{ abortController: AbortController | null }>({ abortController: null });

  // Create appointment modal (doctor creates appointment for this patient)
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);
  const [createAppointmentDoctorId, setCreateAppointmentDoctorId] = useState<number | null>(null);
  const [createAppointmentTimezone, setCreateAppointmentTimezone] = useState<string>('');
  const [createAppointmentPractices, setCreateAppointmentPractices] = useState<DoctorPractice[]>([]);
  const [createAppointmentSelectedPracticeId, setCreateAppointmentSelectedPracticeId] = useState<number | null>(null);
  const [createAppointmentSelectedDate, setCreateAppointmentSelectedDate] = useState<string>('');
  const [createAppointmentDisplayedMonth, setCreateAppointmentDisplayedMonth] = useState<Date>(() => new Date());
  const [createAppointmentLeaveSlots, setCreateAppointmentLeaveSlots] = useState<LeaveSlot[]>([]);
  const [createAppointmentCounts, setCreateAppointmentCounts] = useState<Record<string, number>>({});
  const [createAppointmentCountsLoading, setCreateAppointmentCountsLoading] = useState(false);
  const [createAppointmentSlots, setCreateAppointmentSlots] = useState<AvailableSlotsResponse | null>(null);
  const [createAppointmentSlotsLoading, setCreateAppointmentSlotsLoading] = useState(false);
  const [createAppointmentSelectedSlot, setCreateAppointmentSelectedSlot] = useState<{ time: string; time_24h: string } | null>(null);
  const [createAppointmentTitle, setCreateAppointmentTitle] = useState('Consultation');
  const [createAppointmentType, setCreateAppointmentType] = useState<string>('consultation');
  const [createAppointmentSubmitting, setCreateAppointmentSubmitting] = useState(false);
  const [createAppointmentError, setCreateAppointmentError] = useState<string | null>(null);

  // Profile appointments tab: filter by status
  type AppointmentFilterTab = 'all' | 'upcoming' | 'past' | 'missed_cancelled' | 'reschedule_pending';
  const [appointmentFilterTab, setAppointmentFilterTab] = useState<AppointmentFilterTab>('upcoming');
  const [canJoinStatuses, setCanJoinStatuses] = useState<Map<number, CanJoinCallResponse>>(new Map());
  const [loadingCanJoin, setLoadingCanJoin] = useState<Set<number>>(new Set());

  // Helper to update messages and lastSeenMessageId together
  const updateChatMessages = useCallback((messages: any[]) => {
    setChatMessages(messages);
    if (messages.length > 0) {
      const maxId = Math.max(...messages.map((m) => m.id));
      lastSeenMessageIdRef.current = maxId;
      setLastSeenMessageId(maxId);
    } else {
      lastSeenMessageIdRef.current = 0;
      setLastSeenMessageId(0);
    }
  }, []);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({}); // Track selected option per message
  const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [workflowState, setWorkflowState] = useState<{
    baseIcmrRecommendations: any;
    adjustedRecommendations: any;
    baseIcmrNutrients: any;
    adjustedNutrients: any;
    requirementsPresented: boolean;
    requirementsApproved?: boolean;
    currentStep?: string | null;
    waitingForDoctorResponse?: boolean;
    pendingGoalField?: string | null;
    pendingGoalQuestion?: string | null;
    pendingGoalOptions?: (string | number)[] | null;
    pendingVerificationField?: string | null;
    requiredInputs?: { field_key?: string; question?: string; options?: unknown[] }[];
  } | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showDeletePatientModal, setShowDeletePatientModal] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState(false);
  const [preferences, setPreferences] = useState<MealPlanPreferences>({
    primaryGoal: '',
    secondaryGoals: [],
    dietaryPattern: '',
    cuisinePreferences: [],
    dislikedFoods: [],
    allergies: [],
    medicalConditions: [],
    mealsPerDay: 3,
    cookingTimePerMealMin: 30,
    budgetLevel: 'medium',
    activityLevel: '',
    additionalNotes: '',
  });

  const loadPatientDetails = useCallback(async () => {
    if (!patientId) return;

    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) {
      setError('Invalid patient identifier');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Load patient details (doctor can always view their patient's profile, regardless of email verification)
      const data = await patientsService.getPatientDetails(numericId);
      if (!data) {
        setError('Patient not found');
        return;
      }
      
      setPatient(data);
    } catch (err: any) {
      console.error('Failed to load patient details:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load patient details');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  const handleResendVerification = useCallback(async () => {
    if (!patientId) return;
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) return;
    const email = patient?.email || '';
    try {
      setResendingVerification(true);
      await apiClient.post(`/api/v1/doctors/patients/${numericId}/resend-verification`, {});
      alert(`Verification email has been resent to ${email}`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to resend verification email';
      alert(`Failed to resend verification: ${msg}`);
    } finally {
      setResendingVerification(false);
    }
  }, [patientId, patient?.email]);

  const handleSendReminder = useCallback(async () => {
    if (!patientId) return;
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) return;
    const email = patient?.email || '';
    try {
      setSendingReminder(true);
      await apiClient.post(`/api/v1/doctors/patients/${numericId}/send-reminder`, {});
      alert(`Reminder email has been sent to ${email}`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to send reminder email';
      alert(`Failed to send reminder: ${msg}`);
    } finally {
      setSendingReminder(false);
    }
  }, [patientId, patient?.email]);

  const handleDeletePatient = useCallback(async () => {
    if (!patientId) return;
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) return;
    try {
      setDeletingPatient(true);
      await patientsService.deletePatient(numericId);
      setShowDeletePatientModal(false);
      navigate('/patients');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to delete patient';
      alert(msg);
    } finally {
      setDeletingPatient(false);
    }
  }, [patientId, navigate]);

  // Can-join call for video (profile appointments tab)
  const loadCanJoinStatus = useCallback(async (appointmentId: number, forceRefresh = false) => {
    if (loadingCanJoin.has(appointmentId)) return;
    if (!forceRefresh && canJoinStatuses.has(appointmentId)) return;
    try {
      setLoadingCanJoin(prev => new Set(prev).add(appointmentId));
      const response = await videoConferenceService.canJoinCallForAppointment(appointmentId);
      setCanJoinStatuses(prev => new Map(prev).set(appointmentId, response));
    } catch (err) {
      console.error(`Failed to load can-join status for appointment ${appointmentId}:`, err);
      setCanJoinStatuses(prev => new Map(prev).set(appointmentId, { can_join: false, message: 'Unable to check join status' }));
    } finally {
      setLoadingCanJoin(prev => {
        const next = new Set(prev);
        next.delete(appointmentId);
        return next;
      });
    }
  }, [loadingCanJoin, canJoinStatuses]);

  const canJoinCall = useCallback((appointmentId: number): boolean => {
    return canJoinStatuses.get(appointmentId)?.can_join ?? false;
  }, [canJoinStatuses]);

  const getJoinMessage = useCallback((appointmentId: number): string | undefined => {
    return canJoinStatuses.get(appointmentId)?.message;
  }, [canJoinStatuses]);

  const filteredAppointments = useMemo(() => {
    if (!patient?.appointments?.length) return [];
    const list = patient.appointments;
    const now = new Date();
    if (appointmentFilterTab === 'all') return list;
    if (appointmentFilterTab === 'upcoming') {
      return list.filter((apt) => {
        if (!apt.appointment_date) return false;
        const aptDate = new Date(apt.appointment_date);
        const durationMin = apt.duration_minutes ?? apt.duration ?? 30;
        const endTime = new Date(aptDate.getTime() + durationMin * 60 * 1000);
        const status = apt.status?.toLowerCase() || '';
        return endTime > now && status !== 'completed' && status !== 'cancelled' && status !== 'missed';
      }).sort((a, b) => new Date(a.appointment_date || 0).getTime() - new Date(b.appointment_date || 0).getTime());
    }
    if (appointmentFilterTab === 'past') {
      return list.filter((apt) => (apt.status?.toLowerCase() || '') === 'completed')
        .sort((a, b) => new Date(b.appointment_date || 0).getTime() - new Date(a.appointment_date || 0).getTime());
    }
    if (appointmentFilterTab === 'missed_cancelled') {
      return list.filter((apt) => {
        const s = apt.status?.toLowerCase() || '';
        return s === 'missed' || s === 'cancelled';
      }).sort((a, b) => new Date(b.appointment_date || 0).getTime() - new Date(a.appointment_date || 0).getTime());
    }
    if (appointmentFilterTab === 'reschedule_pending') {
      return list.filter((apt) => (apt.status?.toLowerCase() || '') === 'reschedule_pending')
        .sort((a, b) => new Date(a.appointment_date || 0).getTime() - new Date(b.appointment_date || 0).getTime());
    }
    return list;
  }, [patient?.appointments, appointmentFilterTab]);

  useEffect(() => {
    if (!patient?.appointments?.length || activeTab !== 'appointments') return;
    patient.appointments.forEach((apt) => {
      if (apt.status === 'scheduled' || apt.status === 'confirmed') {
        loadCanJoinStatus(apt.id);
      }
    });
  }, [patient?.appointments, activeTab, loadCanJoinStatus]);

  useEffect(() => {
    loadPatientDetails();
  }, [loadPatientDetails]);

  // Load derived metrics when the metrics or scores tab is active
  useEffect(() => {
    const loadDerived = async () => {
      if (!patientId || (activeTab !== 'metrics' && activeTab !== 'scores')) {
        return;
      }
      const numericPatientId = Number(patientId);
      if (Number.isNaN(numericPatientId)) {
        setDerivedError('Invalid patient identifier for insights');
        return;
      }
      // Only load if we don't already have the data
      if (derivedState || derivedLoading) {
        return;
      }
      try {
        setDerivedLoading(true);
        setDerivedError(null);
        const state = await nutritionProfileService.getCurrentStateForPatient(numericPatientId);
        setDerivedState(state);
      } catch (e: any) {
        console.error('Failed to load insights state', e);
        setDerivedError(e?.response?.data?.detail || e?.message || 'Failed to load insights');
      } finally {
        setDerivedLoading(false);
      }
    };

    const loadDerivedMetricsSummary = async () => {
      if (!patientId) {
        return;
      }
      const numericPatientId = Number(patientId);
      if (isNaN(numericPatientId)) {
        setDerivedMetricsError('Invalid patient identifier for metrics summary');
        return;
      }
      // Only load if we don't already have the data or a previous error (avoid retry loop on 500)
      if (derivedMetricsSummary || derivedMetricsLoading || derivedMetricsError) {
        return;
      }
      try {
        setDerivedMetricsLoading(true);
        setDerivedMetricsError(null);
        const summary = await derivedMetricsService.getSummary(numericPatientId);
        setDerivedMetricsSummary(summary);
      } catch (e: any) {
        console.error('Failed to load derived metrics summary', e);
        setDerivedMetricsError(e?.response?.data?.detail || e?.message || 'Failed to load metrics summary');
      } finally {
        setDerivedMetricsLoading(false);
      }
    };

    void loadDerived();
    void loadDerivedMetricsSummary();
  }, [activeTab, patientId, derivedState, derivedLoading, derivedMetricsSummary, derivedMetricsLoading, derivedMetricsError]);

  // Reset derived metrics state when patient changes so we load for the new patient and don't retry forever on error
  useEffect(() => {
    if (!patientId) return;
    setDerivedMetricsSummary(null);
    setDerivedMetricsError(null);
  }, [patientId]);

  // Sync insights sub-tab when activeTab changes; clear any stale save/error message when opening Insights tab
  useEffect(() => {
    if (activeTab === 'metrics') {
      setInsightsSubTab('metrics');
      setInsightsMessage(null);
    } else if (activeTab === 'scores') {
      setInsightsSubTab('scores');
      setInsightsMessage(null);
    }
  }, [activeTab]);

  // Restore active tab from URL or localStorage on mount/patient change
  useEffect(() => {
    if (!patientId) return;
    const restoredTab = loadActiveTab(patientId);
    setActiveTabState(restoredTab);
    // Update URL if not already set
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('tab') !== restoredTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', restoredTab);
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);
  
  // Handle tab query parameter from URL changes (when user navigates with back/forward)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam === 'mealPlan' || tabParam === 'nutritionProfile' || tabParam === 'appointments') {
      setActiveTabState(tabParam);
      if (patientId) {
        localStorage.setItem(getActiveTabStorageKey(patientId), tabParam);
      }
    }
  }, [location.search, patientId]);

  // Open requirements chat when navigating from diet plan sidebar "View conversation" (?tab=mealPlan&viewConversation=sessionId)
  useEffect(() => {
    if (!patientId) return;
    const searchParams = new URLSearchParams(location.search);
    const viewConversation = searchParams.get('viewConversation');
    if (searchParams.get('tab') === 'mealPlan' && viewConversation) {
      const sessionId = Number(viewConversation);
      if (!Number.isNaN(sessionId) && sessionId > 0) {
        setRequirementsChatSessionId(sessionId);
        saveChatSessionId(patientId, sessionId);
        setShowRequirementsChat(true);
        setActiveTab('mealPlan');
      }
    }
  }, [location.search, patientId]);

  const openCreateAppointmentModal = useCallback(async () => {
    setCreateAppointmentError(null);
    setCreateAppointmentSlots(null);
    setCreateAppointmentSelectedSlot(null);
    setCreateAppointmentTitle('Consultation');
    setCreateAppointmentType('consultation');
    try {
      const [doc, profile, practicesData] = await Promise.all([
        getCurrentDoctor(),
        profileService.getProfile(),
        profileService.getPractices(),
      ]);
      setCreateAppointmentDoctorId(doc.id);
      setCreateAppointmentTimezone(profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      setCreateAppointmentPractices(practicesData);
      const primary = practicesData.find((p) => p.is_primary) || practicesData[0];
      setCreateAppointmentSelectedPracticeId(primary?.id ?? null);
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10);
      setCreateAppointmentSelectedDate(dateStr);
      setCreateAppointmentDisplayedMonth(new Date(today.getFullYear(), today.getMonth(), 1));
      setCreateAppointmentLeaveSlots([]);
      setCreateAppointmentCounts({});
      setShowCreateAppointmentModal(true);
    } catch (e: any) {
      setCreateAppointmentError(e?.response?.data?.detail || e?.message || 'Failed to load');
    }
  }, []);

  // Open create appointment modal when navigating from PatientAppointmentsPage with ?createAppointment=1
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('tab') === 'appointments' && searchParams.get('createAppointment') === '1') {
      searchParams.delete('createAppointment');
      const newSearch = searchParams.toString();
      navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
      openCreateAppointmentModal();
    }
  }, [location.search, location.pathname, navigate, openCreateAppointmentModal]);

  useEffect(() => {
    if (!showCreateAppointmentModal || !createAppointmentDoctorId || !createAppointmentSelectedDate) return;
    let cancelled = false;
    setCreateAppointmentSlotsLoading(true);
    setCreateAppointmentSlots(null);
    setCreateAppointmentSelectedSlot(null);
    dashboardService
      .getAvailableSlots(
        createAppointmentDoctorId,
        createAppointmentSelectedDate,
        createAppointmentSelectedPracticeId ?? undefined
      )
      .then((data) => {
        if (!cancelled) {
          setCreateAppointmentSlots(data);
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          setCreateAppointmentError(e?.response?.data?.detail || e?.message || 'Failed to load slots');
        }
      })
      .finally(() => {
        if (!cancelled) setCreateAppointmentSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreateAppointmentModal, createAppointmentDoctorId, createAppointmentSelectedDate, createAppointmentSelectedPracticeId]);

  // Load leave slots and appointment counts for create-appointment calendar when modal is open
  const createAppointmentFormatDateString = useCallback((date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  useEffect(() => {
    if (!showCreateAppointmentModal) return;
    const month = createAppointmentDisplayedMonth;
    const calendar = new Date(month.getFullYear(), month.getMonth(), 1);
    const monthStart = new Date(calendar);
    const monthEnd = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);
    const startDateStr = createAppointmentFormatDateString(monthStart);
    const endDateStr = createAppointmentFormatDateString(monthEnd);
    let cancelled = false;
    setCreateAppointmentCountsLoading(true);
    Promise.all([
      leaveSlotService.getLeaveSlots({ start_date: startDateStr, end_date: endDateStr }),
      leaveSlotService.getAppointmentCounts(startDateStr, endDateStr),
    ])
      .then(([slots, counts]) => {
        if (!cancelled) {
          setCreateAppointmentLeaveSlots(slots);
          setCreateAppointmentCounts((prev) => ({ ...prev, ...counts }));
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load calendar data:', err);
      })
      .finally(() => {
        if (!cancelled) setCreateAppointmentCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreateAppointmentModal, createAppointmentDisplayedMonth, createAppointmentFormatDateString]);

  // Calendar: only highlight dates when the *selected* practice has timings (not all practices)
  const createAppointmentPracticeDayNames = useMemo(() => {
    const set = new Set<string>();
    const practicesToUse =
      createAppointmentSelectedPracticeId != null
        ? createAppointmentPractices.filter((p) => p.id === createAppointmentSelectedPracticeId)
        : createAppointmentPractices;
    practicesToUse.forEach((practice) => {
      practice.timings?.forEach((t) => {
        if (t.is_available && t.day_of_week) {
          set.add(t.day_of_week.toLowerCase());
        }
      });
    });
    return set;
  }, [createAppointmentPractices, createAppointmentSelectedPracticeId]);

  const createAppointmentLeaveSlotsForDate = useCallback(
    (date: Date): LeaveSlot[] => {
      const dateStr = createAppointmentFormatDateString(date);
      return createAppointmentLeaveSlots.filter((slot) => slot.leave_date === dateStr);
    },
    [createAppointmentLeaveSlots, createAppointmentFormatDateString]
  );

  const createAppointmentDurationInHours = useCallback((start: string, end: string): number => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    return (endHour * 60 + endMin - (startHour * 60 + startMin)) / 60;
  }, []);

  const createAppointmentHasFullDayLeave = useCallback(
    (date: Date): boolean => {
      const slots = createAppointmentLeaveSlotsForDate(date).filter(
        (slot) => slot.status.toLowerCase() !== 'cancelled'
      );
      return (
        slots.some((slot) => slot.start_time === '00:00' && slot.end_time === '23:59') ||
        slots.reduce((total, slot) => {
          return total + createAppointmentDurationInHours(slot.start_time, slot.end_time);
        }, 0) >= 8.0
      );
    },
    [createAppointmentLeaveSlotsForDate, createAppointmentDurationInHours]
  );

  const createAppointmentHasPartialLeave = useCallback(
    (date: Date): boolean => {
      const slots = createAppointmentLeaveSlotsForDate(date).filter(
        (slot) => slot.status.toLowerCase() !== 'cancelled'
      );
      return slots.length > 0 && !createAppointmentHasFullDayLeave(date);
    },
    [createAppointmentLeaveSlotsForDate, createAppointmentHasFullDayLeave]
  );

  const createAppointmentIsDateEnabled = useCallback(
    (date: Date): boolean => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);
      if (normalizedDate < today) return false;
      if (createAppointmentPractices.length === 0) return false;
      if (createAppointmentPracticeDayNames.size === 0) return true;
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[normalizedDate.getDay()];
      return createAppointmentPracticeDayNames.has(dayName);
    },
    [createAppointmentPractices, createAppointmentPracticeDayNames]
  );

  const submitCreateAppointment = useCallback(async () => {
    if (!patientId || !createAppointmentSelectedDate || !createAppointmentSelectedSlot || !createAppointmentDoctorId || !createAppointmentSlots) return;
    setCreateAppointmentSubmitting(true);
    setCreateAppointmentError(null);
    try {
      const slotDuration = createAppointmentSlots.available_dates?.find((d) => d.date === createAppointmentSelectedDate)?.slot_duration ?? 30;
      const appointmentDate = `${createAppointmentSelectedDate}T${createAppointmentSelectedSlot.time_24h}:00`;
      await dashboardService.createAppointmentForPatient(parseInt(patientId, 10), {
        title: createAppointmentTitle,
        appointment_date: appointmentDate,
        duration_minutes: slotDuration,
        appointment_type: createAppointmentType,
        timezone: createAppointmentTimezone || undefined,
      });
      setShowCreateAppointmentModal(false);
      await loadPatientDetails();
    } catch (e: any) {
      setCreateAppointmentError(e?.response?.data?.detail || e?.message || 'Failed to create appointment');
    } finally {
      setCreateAppointmentSubmitting(false);
    }
  }, [
    patientId,
    createAppointmentSelectedDate,
    createAppointmentSelectedSlot,
    createAppointmentDoctorId,
    createAppointmentTitle,
    createAppointmentType,
    createAppointmentTimezone,
    createAppointmentSlots,
    loadPatientDetails,
  ]);

  const loadMealPlan = useCallback(
    async (numericId: number) => {
      try {
        setMealPlanLoading(true);
        setMealPlanError(null);
        
        // Load all meal plans
        const allPlans = await patientsService.getAllPatientMealPlans(numericId);
        console.log('loadMealPlan: Loaded plans:', { count: allPlans.length, plans: allPlans });
        setAllMealPlans(allPlans);
        
        setIsMealPlanEditMode(false); // Start in view mode when meal plan exists
      } catch (err: any) {
        // Handle 404 as a normal case (no meal plan exists yet)
        if (err?.response?.status === 404) {
          console.log('loadMealPlan: 404 - no meal plans found (normal case)');
          setAllMealPlans([]);
          setIsMealPlanEditMode(false);
          return;
        }
        console.error('Failed to load patient meal plan:', err);
        setMealPlanError(
          err?.response?.data?.detail || err?.message || 'Failed to load meal plan',
        );
      } finally {
        setMealPlanLoading(false);
      }
    },
    [],
  );

  const handleDeleteMealPlan = useCallback(
    async (plan: PatientMealPlan) => {
      if (!patientId || plan.createdByDoctorId !== currentDoctorId) return;
      if (!window.confirm(`Delete diet plan "${plan.goalName}"? This cannot be undone.`)) return;
      const numericId = Number(patientId);
      if (Number.isNaN(numericId)) return;
      try {
        setDeletingGoalId(plan.goalId);
        await patientsService.deletePatientMealPlan(numericId, plan.goalId);
        await loadMealPlan(numericId);
      } catch (err: any) {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to delete plan';
        setMealPlanError(msg);
      } finally {
        setDeletingGoalId(null);
      }
    },
    [patientId, currentDoctorId, loadMealPlan],
  );

  const handleViewPlanConversation = useCallback((sessionId: number) => {
    setRequirementsChatSessionId(sessionId);
    if (patientId) saveChatSessionId(patientId, sessionId);
    setShowRequirementsChat(true);
  }, [patientId]);

  // Restore chat session from localStorage on mount or patient change
  useEffect(() => {
    if (!patientId) {
      // Clear session if no patient ID
      setRequirementsChatSessionId(null);
      setChatMessages([]);
    lastSeenMessageIdRef.current = 0;
    setLastSeenMessageId(0);
      lastSeenMessageIdRef.current = 0;
      setLastSeenMessageId(0);
      return;
    }
    
    const storedSessionId = loadChatSessionId(patientId);
    if (storedSessionId) {
      // Verify session still exists (even if no messages yet)
      const verifyAndRestoreSession = async () => {
        try {
          const messages = await patientsService.getChatMessages(storedSessionId);
          // If we can load messages (even if empty array), the session is still valid
          if (messages !== null && Array.isArray(messages)) {
            console.log('Restoring chat session from localStorage:', storedSessionId, 'with', messages.length, 'messages');
            setRequirementsChatSessionId(storedSessionId);
            updateChatMessages(messages);
            // Restore meal plan tab if chat session exists
            const currentTab = loadActiveTab(patientId);
            if (currentTab !== 'mealPlan') {
              setActiveTab('mealPlan');
            }
          } else {
            // Invalid response format, clear it
            console.log('Chat session returned invalid response, clearing:', storedSessionId);
            saveChatSessionId(patientId, null);
            setRequirementsChatSessionId(null);
          }
        } catch (err) {
          // Session doesn't exist or is invalid, clear it
          console.log('Chat session no longer valid, clearing:', storedSessionId, err);
          saveChatSessionId(patientId, null);
          setRequirementsChatSessionId(null);
        }
      };
      void verifyAndRestoreSession();
    } else {
      // No stored session, clear state
      setRequirementsChatSessionId(null);
      setChatMessages([]);
    lastSeenMessageIdRef.current = 0;
    setLastSeenMessageId(0);
      lastSeenMessageIdRef.current = 0;
      setLastSeenMessageId(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Check for nutrition profile when meal plan tab is active
  useEffect(() => {
    if (activeTab !== 'mealPlan' || !patientId) {
      return;
    }
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) {
      return;
    }
    
    // Check if nutrition profile exists (using lightweight status endpoint)
    const checkNutritionProfile = async () => {
      try {
        await nutritionProfileService.getProfileStatus(numericId);
        // Status check complete - profile exists or doesn't
        // Full profile will be loaded when needed
      } catch (err) {
        console.error('Failed to check nutrition profile status:', err);
      }
    };
    
    void checkNutritionProfile();
    void loadMealPlan(numericId);
  }, [activeTab, patientId, loadMealPlan]);

  // Load current doctor when on meal plan tab (for delete-by-creator and display)
  useEffect(() => {
    if (activeTab !== 'mealPlan') return;
    let cancelled = false;
    getCurrentDoctor()
      .then((doc) => {
        if (!cancelled) setCurrentDoctorId(doc.id);
      })
      .catch(() => {
        if (!cancelled) setCurrentDoctorId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const clearRequirementsChatSession = useCallback(async (reason: string) => {
    console.log('🧹 Clearing requirements chat session:', { reason, sessionId: requirementsChatSessionId });
    const sessionIdToCancel = requirementsChatSessionId;

    if (patientId) {
      saveChatSessionId(patientId, null);
    }

    // Clear UI state first (this closes WS + stops polling via useEffects)
    setRequirementsChatSessionId(null);
    setShowRequirementsChat(false);
    setChatMessages([]);
    lastSeenMessageIdRef.current = 0;
    setLastSeenMessageId(0);
    setChatMessageInput('');
    setSelectedOptions({}); // Clear selected options
    setMealPlanLoading(false);
    setChatWsConnected(false);
    setWorkflowState(null); // Clear recommendations comparison table

    // Best-effort server cleanup (drops in-memory workflow state)
    if (sessionIdToCancel) {
      try {
        await patientsService.cancelRequirementsChatSession(sessionIdToCancel);
      } catch (e) {
        // Don't block UX on cleanup errors
        console.warn('Failed to cancel server workflow state (non-fatal):', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, requirementsChatSessionId]);

  const handleCreateMealPlan = async () => {
    if (!patientId) return;
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) {
      setMealPlanError('Invalid patient identifier');
      return;
    }

    // If an old chat session is still active, clear it before creating a new plan
    if (requirementsChatSessionId || workflowState) {
      await clearRequirementsChatSession('create_new_plan_clicked');
    }
    
    // Ensure workflowState is cleared when starting new plan
    setWorkflowState(null);

    // Check if nutrition profile exists before creating meal plan (using lightweight status endpoint)
    try {
      const status = await nutritionProfileService.getProfileStatus(numericId);
      if (!status.hasProfile) {
        setMealPlanError('Nutrition profile is required to create a meal plan. Please create a nutrition profile first.');
        setIsMealPlanEditMode(false);
        setActiveTab('nutritionProfile'); // Redirect to nutrition profile tab
        return;
      }
    } catch (err) {
      console.error('Failed to check nutrition profile status:', err);
      setMealPlanError('Unable to verify nutrition profile. Please try again.');
      return;
    }

    try {
      setMealPlanError(null);
      
      // Show chat interface immediately (optimistically) with "collecting information" state
      // Keep wizard mode active while the requirements workflow runs.
      // (If we flip this to false, the UI re-renders the "All Diet Plans" list mid-flow.)
      setActiveTab('mealPlan'); // Ensure meal plan tab is active
      setMealPlanLoading(true); // Show "collecting information" state
      setShowRequirementsChat(true); // Show chat interface immediately
      
      // Show initial "collecting information" message immediately
      setChatMessages([{
        id: 'temp-1',
        role: 'assistant',
        content: 'I\'m collecting information to calculate appropriate nutrition requirements. This may take a moment...\n\nIf I need any additional information, I\'ll ask you questions here.',
        created_at: new Date().toISOString(),
      }]);
      
      // Make API call in background
      const result = await patientsService.createPatientMealPlan(
        numericId,
        preferences.primaryGoal || 'Adaptive Meal Guidance plan for this patient',
        preferences.additionalNotes,
        preferences,
      );
      
      console.log('Meal plan creation result:', result);
      console.log('Result keys:', Object.keys(result));
      console.log('Result status:', result.status);
      console.log('Result session_id:', result.session_id);
      
      // ALWAYS show chat interface if session_id is present (workflow is interactive)
      const sessionId = result.session_id;
      
      if (sessionId) {
        console.log('✅ Session ID received, updating chat interface:', sessionId);
        
        // Update with real session ID
        setRequirementsChatSessionId(sessionId);
        saveChatSessionId(patientId, sessionId); // Persist to localStorage
        setMealPlanLoading(false); // Stop loading state
        
        // Load actual messages from backend
        try {
          console.log('📥 Loading messages for session:', sessionId);
          const messages = await patientsService.getChatMessages(sessionId);
          console.log('✅ Loaded initial messages:', messages.length, 'messages');
          if (messages && Array.isArray(messages) && messages.length > 0) {
            console.log('📨 Messages content:', messages.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 50) + '...' })));
            updateChatMessages(messages); // Replace temp message with actual messages
          } else {
            console.warn('⚠️ Messages response is not an array or is empty:', messages);
            // Keep the collecting information message if no messages yet
          }
        } catch (msgErr: any) {
          const msgStatus = msgErr?.response?.status;
          console.error('❌ Failed to load initial messages:', msgErr);
          console.error('Error details:', msgErr?.response?.data || msgErr?.message);
          // Don't show 503 errors - they're likely from WebSocket/Lambda issues
          // Polling will retry and eventually load messages
          if (msgStatus !== 503) {
            // Only log non-503 errors, don't show to user
            console.warn('⚠️ Message loading failed (non-503), will retry via polling');
          }
          // Keep the collecting information message if loading fails
        }
        
        // If meal plan was already created, reload it
        if (result.success && result.goal_id) {
          await loadMealPlan(numericId);
        }
        
        console.log('✅ Chat interface should now be visible with actual messages');
      } else if (result.success && result.goal_id) {
        // Meal plan created successfully without interactive workflow
        setMealPlanLoading(false);
        setShowRequirementsChat(false); // Hide chat interface
        setRequirementsChatSessionId(null);
        await loadMealPlan(numericId);
        setIsMealPlanEditMode(false);
      } else {
        // Unexpected response
        console.warn('Unexpected response from meal plan creation:', result);
        setMealPlanError('Unexpected response from server. Please try again.');
        setMealPlanLoading(false);
        setShowRequirementsChat(false); // Hide chat interface
        setRequirementsChatSessionId(null);
        setIsMealPlanEditMode(true); // Stay in wizard so doctor can retry without jumping back to list
      }
    } catch (err: any) {
      console.error('Error creating meal plan:', err);
      const status = err?.response?.status;
      const responseData = err?.response?.data;
      const errorMessage = responseData?.detail || err?.message || 'Failed to create meal plan';
      
      // Handle 503 errors - often gateway timeouts even when backend succeeded
      if (status === 503) {
        console.warn('⚠️ [MealPlanCreate] Received 503 error - likely gateway timeout. Checking if workflow is active...');
        
        // Check if response contains partial data (sometimes gateway returns partial response)
        const sessionId = responseData?.session_id || responseData?.data?.session_id;
        
        if (sessionId) {
          console.log('✅ [MealPlanCreate] Found session_id in 503 response, workflow is active:', sessionId);
          // Workflow is active - set up chat interface
          setRequirementsChatSessionId(sessionId);
          saveChatSessionId(patientId, sessionId);
          setMealPlanError(null);
          setMealPlanLoading(false);
          // Keep chat interface open - polling will work
          return;
        }
        
        // Try to recover by checking if chat session was created
        // The backend creates the session early, so we can try to find it
        const savedSessionId = loadChatSessionId(patientId);
        if (savedSessionId) {
          console.log('✅ [MealPlanCreate] Found saved session_id, attempting recovery:', savedSessionId);
          // Verify the session exists and has messages
          try {
            const messages = await patientsService.getChatMessages(savedSessionId);
            if (messages && messages.length > 0) {
              console.log('✅ [MealPlanCreate] Session verified, workflow is active');
              setRequirementsChatSessionId(savedSessionId);
              updateChatMessages(messages);
              setMealPlanError(null);
              setMealPlanLoading(false);
              return;
            }
          } catch (verifyErr) {
            console.warn('⚠️ [MealPlanCreate] Saved session verification failed:', verifyErr);
          }
        }
        
        // If we can't recover, show a helpful message
        setMealPlanError('Request timed out, but the workflow may still be processing. Please check back in a moment or try again.');
        setMealPlanLoading(false);
        // Don't hide chat interface - user might want to check status
        return;
      }
      
      setMealPlanError(errorMessage);
      setMealPlanLoading(false);
      setShowRequirementsChat(false); // Hide chat interface on error
      setRequirementsChatSessionId(null);
      setIsMealPlanEditMode(true); // Stay in wizard on error
    }
  };

  // Long-polling for chat messages and status updates (primary method)
  const startChatPolling = useCallback((sessionId: number) => {
    // Cancel any existing polling
    if (chatPollingRef.current.abortController) {
      chatPollingRef.current.abortController.abort();
    }

    const abortController = new AbortController();
    chatPollingRef.current.abortController = abortController;

    const poll = async () => {
      if (abortController.signal.aborted || document.visibilityState === 'hidden') {
        return;
      }

      try {
        const syncResponse = await patientsService.syncMessages(
          sessionId,
          lastSeenMessageIdRef.current,
          25 // 25 second timeout
        );

        if (abortController.signal.aborted) return;

        // Update messages if new ones arrived
        if (syncResponse.messages && syncResponse.messages.length > 0) {
          setChatMessages((prev) => {
            // Merge new messages, avoiding duplicates
            const existingIds = new Set(prev.map((m) => m.id));
            const newMessages = syncResponse.messages.filter((m: any) => !existingIds.has(m.id));
            return [...prev, ...newMessages].sort((a, b) => a.id - b.id);
          });
          // Update last seen message ID
          const maxId = Math.max(...syncResponse.messages.map((m: any) => m.id));
          lastSeenMessageIdRef.current = Math.max(lastSeenMessageIdRef.current, maxId);
          setLastSeenMessageId(lastSeenMessageIdRef.current);
          // Clear sending state when new messages arrive
          setSendingMessage(false);
        } else {
          // Update last seen from response even if no new messages
          lastSeenMessageIdRef.current = syncResponse.next_since;
          setLastSeenMessageId(syncResponse.next_since);
        }

        // Handle status updates
        if (syncResponse.status_updates && syncResponse.status_updates.length > 0) {
          const latestStatus = syncResponse.status_updates[syncResponse.status_updates.length - 1];
          setCurrentStatus({
            status: latestStatus.status,
            progress: latestStatus.progress,
            agentName: latestStatus.agent_name,
          });
          // Clear status when complete
          if (latestStatus.status === 'complete' || latestStatus.status === 'message_added') {
            setTimeout(() => setCurrentStatus(null), 2000);
          }
        }

        // Continue polling: if backend returned immediately (no new data), wait before next request to avoid hammering
        const hadNewData =
          (syncResponse.messages && syncResponse.messages.length > 0) ||
          (syncResponse.status_updates && syncResponse.status_updates.length > 0);
        const minDelayMs = hadNewData ? 0 : 2000; // 2s between polls when no new data (e.g. WS disconnected)
        if (!abortController.signal.aborted) {
          if (minDelayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, minDelayMs));
          }
          if (!abortController.signal.aborted) {
            poll();
          }
        }
      } catch (err: any) {
        if (abortController.signal.aborted) return;

        const status = err?.response?.status;
        // Don't log 503 errors - they're expected when backend is unavailable
        if (status !== 503 && status !== 0) {
          console.error('Failed to sync messages:', err);
        }

        // Exponential backoff on error
        const backoffMs = 1000; // Start with 1 second
        await new Promise((resolve) => setTimeout(resolve, backoffMs));

        // Continue polling after backoff
        if (!abortController.signal.aborted) {
          poll();
        }
      }
    };

    // Start polling
    poll();

    // Cleanup function
    return () => {
      abortController.abort();
      chatPollingRef.current.abortController = null;
    };
  }, []);

  const buildChatWsUrl = useCallback((sessionId: number) => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    return getChatStatusWebSocketUrl(sessionId, apiBase);
  }, []);

  // Open a WebSocket for real-time chat notifications (message_added) - optional, guarded by flag
  useEffect(() => {
    // Skip WebSocket if flag is disabled
    if (!USE_CHAT_WEBSOCKET) {
      return;
    }

    // Close any existing socket when session changes / clears
    if (!requirementsChatSessionId) {
      if (chatWsRef.current) {
        try {
          chatWsRef.current.close();
        } catch {
          // ignore
        }
        chatWsRef.current = null;
      }
      setChatWsConnected(false);
      return;
    }

    const sessionId = requirementsChatSessionId;
    const { url: wsUrl, sendFirstMessage } = buildChatWsUrl(sessionId);
    console.log('🔌 [MealPlanChat WS] Connecting:', wsUrl);

    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(wsUrl);
      chatWsRef.current = ws;
    } catch (e) {
      console.error('❌ [MealPlanChat WS] Failed to create WebSocket:', e);
      setChatWsConnected(false);
      return;
    }

    ws.onopen = () => {
      console.log('✅ [MealPlanChat WS] Connected for session', sessionId);
      setChatWsConnected(true);
      if (sendFirstMessage) {
        try {
          ws.send(JSON.stringify({ session_id: sessionId }));
        } catch (e) {
          console.warn('[MealPlanChat WS] Failed to send session_id first message:', e);
        }
      }
    };

    ws.onmessage = (event) => {
      try {
        const statusMessage = JSON.parse(event.data);
        // Backend sends ChatStatusMessage: { session_id, status, message (JSON string), progress, agent_name }
        const status = statusMessage?.status;
        let eventData: any = null;
        if (statusMessage?.message) {
          try {
            eventData =
              typeof statusMessage.message === 'string'
                ? JSON.parse(statusMessage.message)
                : statusMessage.message;
          } catch {
            eventData = null;
          }
        }

        if (status === 'message_added' || status === 'new_message' || eventData?.message_type === 'message_added') {
          console.log('📨 [MealPlanChat WS] message notification received; reloading messages', { status, eventData });
          // Clear sending state since agent has responded
          setSendingMessage(false);
          // Single source of truth: reload from backend
          void patientsService
            .getChatMessages(sessionId)
            .then((messages) => updateChatMessages(messages))
            .catch((err) => console.error('❌ [MealPlanChat WS] Failed to reload messages:', err));
        }
      } catch (err) {
        console.error('❌ [MealPlanChat WS] Failed to parse WebSocket message:', err, event.data);
      }
    };

    ws.onerror = (e) => {
      console.error('❌ [MealPlanChat WS] WebSocket error:', e);
      // Don't show error to user - WebSocket is optional, polling will handle updates
      // WebSocket failures (e.g., 503 via Lambda/API Gateway) are non-critical
      setChatWsConnected(false);
    };

    ws.onclose = (e) => {
      console.log('🔌 [MealPlanChat WS] WebSocket closed:', e.code, e.reason);
      setChatWsConnected(false);
      if (chatWsRef.current === ws) {
        chatWsRef.current = null;
      }
      // If WebSocket closes unexpectedly (e.g., 503), polling will continue to work
      // No need to show error to user - this is expected when WebSocket is unavailable
    };

    return () => {
      try {
        ws?.close();
      } catch {
        // ignore
      }
      if (chatWsRef.current === ws) {
        chatWsRef.current = null;
      }
      setChatWsConnected(false);
    };
  }, [requirementsChatSessionId, buildChatWsUrl, updateChatMessages]);

  // Start long-polling when session is available (do not depend on chatMessages to avoid restarting on every update)
  useEffect(() => {
    if (!requirementsChatSessionId) {
      // Stop polling when session is cleared
      if (chatPollingRef.current.abortController) {
        chatPollingRef.current.abortController.abort();
        chatPollingRef.current.abortController = null;
      }
      setCurrentStatus(null);
      return;
    }

    // Reset lastSeenMessageId when starting a new session; first sync response will set next_since
    lastSeenMessageIdRef.current = 0;
    setLastSeenMessageId(0);

    // Start long-polling
    const cleanup = startChatPolling(requirementsChatSessionId);

    return cleanup;
  }, [requirementsChatSessionId, startChatPolling]);

  const handleSendChatMessage = async (overrideMessage?: string) => {
    const text = (overrideMessage != null ? overrideMessage : chatMessageInput).trim();
    if (!requirementsChatSessionId || !text || sendingMessage) return;

    try {
      const messageToSend = text;
      // Optimistic UX: clear input immediately and show the doctor message
      setChatMessageInput('');
      // Clear selected options for all messages since we're sending a new response
      setSelectedOptions({});
      setChatMessages((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          role: 'user',
          sender_type: 'doctor',
          content: messageToSend,
          created_at: new Date().toISOString(),
        },
      ]);
      // Immediately scroll to the bottom so the just-sent message is visible
      requestAnimationFrame(() => {
        const container = chatMessagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
      setSendingMessage(true);
      const result = await patientsService.sendRequirementsChatMessage(
        requirementsChatSessionId,
        messageToSend,
      );
      
      // Refresh messages
      const messages = await patientsService.getChatMessages(requirementsChatSessionId);
      updateChatMessages(messages);
      // Clear sending state since we've received the response
      setSendingMessage(false);
      
      // If workflow completed, reload meal plan but DON'T auto-close the window
      // Let the doctor close it manually using the close button
      if (result.success && result.goal_id) {
        await loadMealPlan(Number(patientId));
        // Keep the chat open - doctor can close it manually
        // Don't call clearRequirementsChatSession here
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to send message';

      // If backend lost the in-memory workflow state (container restart / expired session),
      // immediately release frontend resources and force starting a new plan.
      if (status === 404 && typeof detail === 'string' && detail.includes('No active requirements workflow found')) {
        await clearRequirementsChatSession('workflow_state_missing_404');
        setIsMealPlanEditMode(true);
        setMealPlanLoading(false);
        setMealPlanError('This chat session expired (server restarted or workflow state cleared). Please click "Create Plan" again.');
        return;
      }

      setMealPlanError(detail);
    } finally {
      setSendingMessage(false);
    }
  };

  // Load initial messages when chat session is set (long-polling will handle updates)
  useEffect(() => {
    if (requirementsChatSessionId) {
      console.log('Chat session ID set, loading initial messages:', requirementsChatSessionId);
      patientsService.getChatMessages(requirementsChatSessionId)
        .then((messages) => {
          console.log('Initial messages loaded:', messages.length);
          updateChatMessages(messages);
        })
        .catch((err: any) => {
          const status = err?.response?.status;
          // Don't log 503 errors - they're expected when backend is unavailable
          // Long-polling will handle retries
          if (status !== 503 && status !== 0) {
            console.error('Failed to load initial messages:', err);
          }
        });
    }
  }, [requirementsChatSessionId, updateChatMessages]);

  // Load workflow state when chat session is active
  const loadWorkflowState = useCallback(async () => {
    if (!requirementsChatSessionId) return;
    
    try {
      const state = await patientsService.getRequirementsChatState(requirementsChatSessionId);
      if (state.success && state.has_state) {
        setWorkflowState({
          baseIcmrRecommendations: state.base_icmr_recommendations,
          adjustedRecommendations: state.adjusted_recommendations,
          baseIcmrNutrients: state.base_icmr_nutrients,
          adjustedNutrients: state.adjusted_nutrients,
          requirementsPresented: state.requirements_presented || false,
          requirementsApproved: state.requirements_approved || false,
          currentStep: state.current_step ?? null,
          waitingForDoctorResponse: Boolean(state.waiting_for_doctor_response),
          pendingGoalField: state.pending_goal_field ?? null,
          pendingGoalQuestion: state.pending_goal_question ?? null,
          pendingGoalOptions: state.pending_goal_options ?? null,
          pendingVerificationField: state.pending_verification_field ?? null,
          requiredInputs: state.required_inputs ?? undefined,
        });
      } else {
        setWorkflowState(null);
      }
    } catch (err) {
      console.error('Failed to load workflow state:', err);
      setWorkflowState(null);
    }
  }, [requirementsChatSessionId]);

  // Poll workflow state when requirements are presented
  useEffect(() => {
    if (!requirementsChatSessionId || !workflowState?.requirementsPresented) return;
    
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      loadWorkflowState();
    }, chatWsConnected ? 15000 : 4000); // Prefer WS; polling is a fallback
    
    return () => clearInterval(interval);
  }, [requirementsChatSessionId, workflowState?.requirementsPresented, loadWorkflowState, chatWsConnected]);

  // Load workflow state when chat messages change (agent may have updated requirements)
  useEffect(() => {
    if (requirementsChatSessionId && chatMessages.length > 0) {
      loadWorkflowState();
    }
  }, [requirementsChatSessionId, chatMessages.length, loadWorkflowState]);

  // When state says we're waiting for doctor response, refetch messages so the latest question appears (restore after idle)
  const loadWorkflowStateAndRefetchMessages = useCallback(async () => {
    if (!requirementsChatSessionId) return;
    await loadWorkflowState();
    try {
      const state = await patientsService.getRequirementsChatState(requirementsChatSessionId);
      if (state?.success && state?.has_state && state.waiting_for_doctor_response) {
        const messages = await patientsService.getChatMessages(requirementsChatSessionId);
        if (messages != null && Array.isArray(messages)) updateChatMessages(messages);
      }
    } catch {
      // ignore
    }
  }, [requirementsChatSessionId, loadWorkflowState, updateChatMessages]);

  // On mount or when session is restored (e.g. from localStorage), load state and refetch messages so pending question is visible
  useEffect(() => {
    if (!requirementsChatSessionId) return;
    loadWorkflowStateAndRefetchMessages();
  }, [requirementsChatSessionId, loadWorkflowStateAndRefetchMessages]);

  // Auto-scroll requirements chat to the latest message whenever messages change
  useEffect(() => {
    if (!requirementsChatSessionId) return;
    const container = chatMessagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [requirementsChatSessionId, chatMessages.length]);
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('State update - requirementsChatSessionId:', requirementsChatSessionId, 'chatMessages:', chatMessages.length, 'isMealPlanEditMode:', isMealPlanEditMode);
  }, [requirementsChatSessionId, chatMessages.length, isMealPlanEditMode]);

  const handleEnterEditMode = async () => {
    if (!patientId) return;
    const numericId = Number(patientId);
    if (Number.isNaN(numericId)) {
      return;
    }

    // Starting a new plan: close WS + stop polling by clearing the active chat session
    if (requirementsChatSessionId || workflowState) {
      await clearRequirementsChatSession('enter_edit_mode_create_new');
    }
    
    // Ensure workflowState is cleared when entering edit mode
    setWorkflowState(null);

    // Check if nutrition profile exists before entering edit mode (using lightweight status endpoint)
    try {
      const status = await nutritionProfileService.getProfileStatus(numericId);
      if (!status.hasProfile) {
        setShowNutritionProfileWarning(true);
        setMealPlanError('Nutrition profile is required to create a meal plan. Please create a nutrition profile first.');
        return;
      }
      setIsMealPlanEditMode(true);
      setMealPlanError(null);
      setShowNutritionProfileWarning(false);
    } catch (err) {
      console.error('Failed to check nutrition profile status:', err);
      setMealPlanError('Unable to verify nutrition profile. Please try again.');
    }
  };

  // When navigating from Diet Plans list with ?createPlan=1, open meal plan tab and enter create flow
  useEffect(() => {
    if (!patientId) return;
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('createPlan') !== '1') return;
    setActiveTab('mealPlan');
    // Remove createPlan from URL so we don't re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete('createPlan');
    window.history.replaceState({}, '', url.toString());
    handleEnterEditMode();
  }, [patientId, location.search]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const handleAppointmentClick = (appointment: AppointmentWithDetails) => {
    if (appointment.status.toLowerCase() === 'completed') {
      navigate(`/appointments/${appointment.id}`);
    }
  };

  const patientExtraChildren = useMemo((): SidebarMenuItem[] => {
    if (!patientId || !patient) return [];
    return [
      {
        id: 'patient-appointments',
        label: 'Appointments',
        icon: '',
        onClick: () => navigate(`/patients/${patientId}/appointments`),
        isActive: activeTab === 'appointments',
      },
      {
        id: 'patient-diet-plans',
        label: 'Diet Plans',
        icon: '',
        onClick: () => navigate(`/patients/${patientId}/diet-plans`),
        isActive: activeTab === 'mealPlan',
      },
      {
        id: 'patient-nutrition-profile',
        label: 'Nutrition Profile',
        icon: '',
        onClick: () => navigate(`/patients/${patientId}/nutrition-profile`),
        isActive: activeTab === 'nutritionProfile',
      },
      {
        id: 'patient-insights',
        label: 'Insights',
        icon: '',
        onClick: () => navigate(`/patients/${patientId}/insights`),
        isActive: activeTab === 'metrics' || activeTab === 'scores',
      },
    ];
  }, [
    patientId,
    patient,
    activeTab,
    navigate,
  ]);

  const sidebarContext = patientId && patient
    ? { patientId, patientName: patient.name, patientExtraChildren }
    : undefined;
  const sidebarItems = createSidebarItems(navigate, location.pathname, sidebarContext);

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message">Loading patient details...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="error-message">Error: {error || 'Patient not found'}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="patient-detail-page-wrap">
              <div className="patient-detail-one-card">
            <nav className="patient-detail-breadcrumb" aria-label="Breadcrumb">
              <Link to="/patients" className="breadcrumb-link">Patients</Link>
              <span className="breadcrumb-sep" aria-hidden>›</span>
              <span className="breadcrumb-current">{patient.name}</span>
            </nav>
            {/* Patient Header */}
            <section className="dashboard-section patient-header-section">
              <div className="patient-header">
                <div className="patient-header-avatar">
                  <span>{getInitials(patient.name)}</span>
                </div>
                <div className="patient-header-info">
                  <h1 className="patient-header-name">{patient.name}</h1>
                  {patient.email && (
                    <div className="patient-header-email">{patient.email}</div>
                  )}
                  <div className="patient-header-stats">
                    <span className="header-stat">
                      <span className="header-stat-label">Total Appointments:</span>
                      <span className="header-stat-value">{patient.totalAppointments}</span>
                    </span>
                    {patient.firstAppointmentDate && (
                      <span className="header-stat">
                        <span className="header-stat-label">First Visit:</span>
                        <span className="header-stat-value">{formatDate(patient.firstAppointmentDate)}</span>
                      </span>
                    )}
                    {patient.lastAppointmentDate && (
                      <span className="header-stat">
                        <span className="header-stat-label">Last Visit:</span>
                        <span className="header-stat-value">{formatDate(patient.lastAppointmentDate)}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="patient-header-right">
                  <div className="patient-header-onboarding-status">
                    <span className="header-stat-label">Onboarding:</span>
                    <span
                      className={`header-stat-value onboarding-status-badge onboarding-status-${patient.verificationStatus === 'completed' ? 'completed' : 'incomplete'}`}
                    >
                      {patient.verificationStatus === 'completed' ? 'Completed' : 'Incomplete'}
                    </span>
                  </div>
                  {patient.verificationStatus !== 'completed' && (
                    <div className="patient-header-email-actions">
                      <button
                        type="button"
                        className="patient-email-action-btn resend-verification"
                        onClick={handleResendVerification}
                        disabled={resendingVerification || sendingReminder}
                      >
                        {resendingVerification ? 'Sending...' : 'Resend Verification Link'}
                      </button>
                      <button
                        type="button"
                        className="patient-email-action-btn send-reminder"
                        onClick={handleSendReminder}
                        disabled={resendingVerification || sendingReminder}
                      >
                        {sendingReminder ? 'Sending...' : 'Send Reminder Email'}
                      </button>
                      {patient.createdByDoctorId != null && (
                        <button
                          type="button"
                          className="patient-email-action-btn delete-patient"
                          onClick={() => setShowDeletePatientModal(true)}
                          disabled={deletingPatient}
                        >
                          {deletingPatient ? 'Deleting...' : 'Delete patient'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <ConfirmationModal
              open={showDeletePatientModal}
              title="Delete patient"
              message="This patient has not verified their email. Their account and all associated data will be permanently deleted. This cannot be undone. Continue?"
              confirmLabel={deletingPatient ? 'Deleting...' : 'Delete patient'}
              cancelLabel="Cancel"
              destructive
              onConfirm={handleDeletePatient}
              onCancel={() => !deletingPatient && setShowDeletePatientModal(false)}
            />

            {/* Detail Tabs */}
            <section className="dashboard-section patient-tabs-section">
              <div className="patient-tabs-header">
                <button
                  type="button"
                  className={`patient-tab ${activeTab === 'appointments' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('appointments');
                  }}
                >
                  Appointments
                </button>
                <button
                  type="button"
                  className={`patient-tab ${activeTab === 'mealPlan' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('mealPlan');
                  }}
                >
                  Diet Plans
                </button>
                <button
                  type="button"
                  className={`patient-tab ${activeTab === 'nutritionProfile' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('nutritionProfile');
                  }}
                >
                  Nutrition Profile
                </button>
                <button
                  type="button"
                  className={`patient-tab ${activeTab === 'metrics' || activeTab === 'scores' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('metrics');
                    setInsightsSubTab('metrics');
                  }}
                >
                  Insights
                </button>
              </div>

              <div className="patient-tab-panel" style={{ display: activeTab === 'appointments' ? 'block' : 'none' }}>
                  <div className="section-title-row">
                    <h2 className="section-title">Appointments</h2>
                    <button
                      type="button"
                      className="primary-button create-appointment-btn"
                      onClick={openCreateAppointmentModal}
                    >
                      Create Appointment
                    </button>
                  </div>
                  {patient.appointments.length === 0 ? (
                    <div className="no-appointments">No appointments found for this patient.</div>
                  ) : (
                    <>
                      <div className="patient-appointments-filter-tabs">
                        <button
                          type="button"
                          className={`patient-appointment-filter-tab ${appointmentFilterTab === 'all' ? 'active' : ''}`}
                          onClick={() => setAppointmentFilterTab('all')}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className={`patient-appointment-filter-tab ${appointmentFilterTab === 'upcoming' ? 'active' : ''}`}
                          onClick={() => setAppointmentFilterTab('upcoming')}
                        >
                          Upcoming
                        </button>
                        <button
                          type="button"
                          className={`patient-appointment-filter-tab ${appointmentFilterTab === 'past' ? 'active' : ''}`}
                          onClick={() => setAppointmentFilterTab('past')}
                        >
                          Past
                        </button>
                        <button
                          type="button"
                          className={`patient-appointment-filter-tab ${appointmentFilterTab === 'missed_cancelled' ? 'active' : ''}`}
                          onClick={() => setAppointmentFilterTab('missed_cancelled')}
                        >
                          Missed & Cancelled
                        </button>
                        <button
                          type="button"
                          className={`patient-appointment-filter-tab ${appointmentFilterTab === 'reschedule_pending' ? 'active' : ''}`}
                          onClick={() => setAppointmentFilterTab('reschedule_pending')}
                        >
                          Reschedule Pending
                        </button>
                      </div>
                      <div className="appointments-table-container">
                        <table className="appointments-table">
                          <thead>
                            <tr>
                              <th>Date & Time</th>
                              <th>Type</th>
                              <th>Status</th>
                              <th>Duration</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAppointments.length === 0 ? (
                              <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                  No appointments match this filter.
                                </td>
                              </tr>
                            ) : (
                              filteredAppointments.map((appointment) => {
                                const canJoin = canJoinCall(appointment.id);
                                const joinMessage = getJoinMessage(appointment.id);
                                const isLoadingJoin = loadingCanJoin.has(appointment.id);
                                const showJoinButton =
                                  (appointment.status === 'scheduled' || appointment.status === 'confirmed') &&
                                  (() => {
                                    if (!appointment.appointment_date) return false;
                                    const aptDate = new Date(appointment.appointment_date);
                                    const durationMin = appointment.duration_minutes ?? appointment.duration ?? 30;
                                    const endTime = new Date(aptDate.getTime() + durationMin * 60 * 1000);
                                    return endTime > new Date();
                                  })();
                                return (
                                  <tr key={appointment.id}>
                                    <td>
                                      <div className="appointment-date-cell">
                                        <div className="appointment-date">
                                          {formatDateTime(appointment.appointment_date, true, 'short')}
                                        </div>
                                        <div className="appointment-time">
                                          {formatAppointmentTime(appointment.appointment_date, appointment.duration_minutes ?? appointment.duration)}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="appointment-type-badge">
                                        {getConsultationTypeLabel(appointment.appointment_type)}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={getAppointmentStatusBadgeClass(appointment.status, true)}>
                                        {getAppointmentStatusLabel(appointment.status)}
                                      </span>
                                    </td>
                                    <td>{(appointment.duration_minutes ?? appointment.duration) ? `${appointment.duration_minutes ?? appointment.duration} min` : 'N/A'}</td>
                                    <td>
                                      <div className="appointment-actions">
                                        {appointment.status?.toLowerCase() === 'completed' ? (
                                          <button
                                            className="view-details-btn"
                                            onClick={() => handleAppointmentClick(appointment)}
                                          >
                                            View Details
                                          </button>
                                        ) : (
                                          <>
                                            <div className="appointment-actions-row">
                                              <button
                                                className="action-btn-edit"
                                                onClick={() =>
                                                  navigate(`/appointments/${appointment.id}/edit`, {
                                                    state: { returnToPatientId: patientId },
                                                  })
                                                }
                                              >
                                                Edit
                                              </button>
                                              <button
                                                className="view-details-btn"
                                                onClick={() => navigate(`/appointments/${appointment.id}`)}
                                              >
                                                View
                                              </button>
                                              {showJoinButton && (
                                                <button
                                                  className={`action-btn-join ${!canJoin ? 'disabled' : ''}`}
                                                  onClick={() => canJoin && navigate(`/video-conference/${appointment.id}`)}
                                                  disabled={!canJoin}
                                                >
                                                  <span className="action-icon">📹</span>
                                                  Join Call
                                                </button>
                                              )}
                                            </div>
                                            {showJoinButton && (joinMessage || isLoadingJoin) && (
                                              <div className="join-call-container">
                                                {!canJoin && joinMessage && !isLoadingJoin && (
                                                  <div className="join-call-note">{joinMessage}</div>
                                                )}
                                                {isLoadingJoin && (
                                                  <div className="join-call-note">Checking availability...</div>
                                                )}
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>

              <div className="patient-tab-panel" style={{ display: activeTab === 'mealPlan' ? 'block' : 'none' }}>
                    {!isMealPlanEditMode && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: allMealPlans.length > 0 ? '16px' : '0' }}>
                        {allMealPlans.length > 0 ? (
                          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>All Diet Plans</h3>
                        ) : (
                          <div />
                        )}
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleEnterEditMode}
                      >
                        Create New Plan
                      </button>
                  </div>
                    </div>
                  )}
                  
                  {/* Show all meal plans as cards if there are multiple */}
                  {!isMealPlanEditMode && allMealPlans.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        marginBottom: '24px'
                      }}>
                        {/* Sort plans: active first, then by effective date */}
                        {[...allMealPlans].sort((a, b) => {
                          // Active plans first
                          if (a.isActive && !b.isActive) return -1;
                          if (!a.isActive && b.isActive) return 1;
                          // Then sort by effective date (most recent first)
                          if (a.effectiveAt && b.effectiveAt) {
                            return new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime();
                          }
                          if (a.effectiveAt) return -1;
                          if (b.effectiveAt) return 1;
                          return 0;
                        }).map((plan) => (
                          <div
                            key={plan.goalId}
                            onClick={() => {
                              navigate(`/patients/${patientId}/diet-plan/${plan.goalId}`);
                            }}
                            style={{
                              padding: '16px',
                              border: plan.isActive ? '2px solid #6366f1' : '2px solid #e5e7eb',
                              borderRadius: '12px',
                              backgroundColor: plan.isActive ? '#fef2f2' : '#ffffff',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow: plan.isActive ? '0 2px 8px rgba(220, 38, 38, 0.1)' : 'none',
                            }}
                            onMouseEnter={(e) => {
                              if (!plan.isActive) {
                                e.currentTarget.style.borderColor = '#d1d5db';
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!plan.isActive) {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.backgroundColor = '#ffffff';
                              } else {
                                e.currentTarget.style.borderColor = '#6366f1';
                                e.currentTarget.style.backgroundColor = '#fef2f2';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: plan.isActive ? '#6366f1' : '#111827' }}>
                                  {plan.goalName}
                                </h4>
                                {plan.isActive && (
                                  <span style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#6366f1',
                                    color: '#ffffff',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    boxShadow: '0 1px 3px rgba(220, 38, 38, 0.3)'
                                  }}>
                                    Active
                                  </span>
                                )}
                                {plan.planStatus === 'approved' && (
                                  <span style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#059669',
                                    color: '#ffffff',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                  }}>
                                    Approved
                                  </span>
                                )}
                                {plan.planStatus === 'in_progress' && (
                                  <span style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#d97706',
                                    color: '#ffffff',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                  }}>
                                    In progress
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                                {plan.createdByDoctorId != null && plan.createdByDoctorId === currentDoctorId && !plan.isActive && (
                                  <button
                                    type="button"
                                    className="secondary-button"
                                    style={{ fontSize: '12px', padding: '4px 10px', color: '#dc2626', borderColor: '#dc2626' }}
                                    onClick={() => handleDeleteMealPlan(plan)}
                                    disabled={deletingGoalId === plan.goalId}
                                  >
                                    {deletingGoalId === plan.goalId ? 'Deleting…' : 'Delete'}
                                  </button>
                                )}
                              </div>
                            </div>
                            {plan.effectiveAt && (
                              <p style={{ margin: '4px 0', fontSize: '13px', color: plan.isActive ? '#4338ca' : '#6b7280' }}>
                                Effective: {new Date(plan.effectiveAt).toLocaleDateString()}
                              </p>
                            )}
                            {plan.mealPlan?.totalCaloriesKcal && (
                              <p style={{ margin: '4px 0', fontSize: '14px', color: plan.isActive ? '#4338ca' : '#374151', fontWeight: '500' }}>
                                {Math.round(plan.mealPlan.totalCaloriesKcal)} kcal/day
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show Daily Meal Plan and Recipes Tab when showMealPlans parameter is present or editPlan parameter is present */}
                  {(() => {
                    const searchParams = new URLSearchParams(location.search);
                    const showMealPlans = searchParams.get('showMealPlans');
                    const editPlanId = searchParams.get('editPlan');
                    if ((showMealPlans === 'true' || editPlanId) && patientId) {
                      const numericPatientId = Number(patientId);
                      if (!Number.isNaN(numericPatientId)) {
                        return (
                          <div style={{ marginTop: '24px' }}>
                            <PatientMealPlanTab patientId={numericPatientId} goalId={undefined} />
                          </div>
                        ) as React.ReactElement;
                      }
                    }
                    return null;
                  })()}

                  {/* Show nutrition profile warning only when user tries to create meal plan */}
                  {showNutritionProfileWarning && (
                    <div className="section-card">
                      <div style={{ 
                        padding: '12px 16px', 
                        backgroundColor: '#fef3c7', 
                        border: '1px solid #fbbf24', 
                        borderRadius: '8px', 
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '0.9rem' }}>
                          A nutrition profile is required to create a meal plan. Please create a nutrition profile first.
                        </p>
                        <button
                          type="button"
                          className="primary-button"
                          onClick={() => {
                            setActiveTab('nutritionProfile');
                            setShowNutritionProfileWarning(false);
                          }}
                        >
                          Go to Nutrition Profile
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowNutritionProfileWarning(false)}
                          className="secondary-button"
                          style={{ marginLeft: '12px' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show form when in edit mode (creating or regenerating) AND no active chat */}
                  {isMealPlanEditMode && !requirementsChatSessionId && (
                    <>
                  <div className="meal-plan-intro">
                    <p>
                      Use Adaptive Meal Guidance to create a personalized diet plan for this patient, based on their
                      nutrition profile, preferences, and current intake as described in the Scenario 2 implementation
                      plan.
                    </p>
                  </div>
                  {/* Configuration form */}
                  <div className="meal-plan-config">
                    <div className="meal-plan-config-row">
                      <div className="meal-plan-field-group full-width">
                        <label htmlFor="primaryGoal">Primary goal</label>
                        <input
                          id="primaryGoal"
                          type="text"
                          placeholder="e.g. Weight loss, Diabetes control, Muscle gain"
                          value={preferences.primaryGoal ?? ''}
                          onChange={(e) =>
                            setPreferences((prev) => ({ ...prev, primaryGoal: e.target.value }))
                          }
                        />
                      </div>
                    </div>

                    <div className="meal-plan-config-row">
                      <div className="meal-plan-field-group full-width">
                        <label htmlFor="additionalNotes">Additional notes for dietitian agent</label>
                        <textarea
                          id="additionalNotes"
                          rows={3}
                          placeholder="Any specific constraints, cultural preferences, or clinical notes you want the plan to respect."
                          value={preferences.additionalNotes ?? ''}
                          onChange={(e) =>
                            setPreferences((prev) => ({
                              ...prev,
                              additionalNotes: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  {mealPlanLoading && (
                    <div className="meal-plan-loading">Agent is collecting information...</div>
                  )}
                  {mealPlanError && !mealPlanLoading && (
                    <div className="meal-plan-error">Error: {mealPlanError}</div>
                  )}
                  {!mealPlanLoading && (
                    <div className="meal-plan-actions" style={{ marginTop: '20px', display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        className="meal-plan-primary-btn"
                        onClick={handleCreateMealPlan}
                        disabled={mealPlanLoading}
                        style={{ alignSelf: 'auto' }}
                      >
                        {mealPlanLoading ? 'Creating...' : 'Create Plan'}
                      </button>
                        <button
                          type="button"
                        onClick={() => {
                          setIsMealPlanEditMode(false);
                          setMealPlanError(null);
                          // Clear chat session if cancelling
                          if (requirementsChatSessionId && patientId) {
                              void clearRequirementsChatSession('cancel_clicked');
                          }
                        }}
                        className="secondary-button"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                    </>
                  )}

                  {/* Show chat interface when session ID is set or when collecting information */}
                  {(showRequirementsChat || requirementsChatSessionId) && (() => {
                    // Progress steps (include verification + doctor approval)
                    const steps: Array<{ id: string; label: string; status: 'pending' | 'in_progress' | 'complete' }> = [
                      { id: 'initial', label: 'Initializing', status: 'pending' },
                      { id: 'step1', label: 'Gathering Patient Info', status: 'pending' },
                      { id: 'step2', label: 'Analyzing Objective', status: 'pending' },
                      { id: 'verify', label: 'Verification', status: 'pending' },
                      { id: 'step3', label: 'Calculating Requirements', status: 'pending' },
                      { id: 'review', label: 'Review Requirements', status: 'pending' },
                      { id: 'approve', label: 'Doctor Modification & Approval', status: 'pending' },
                      { id: 'done', label: 'Done', status: 'pending' },
                    ];

                    // Signals from backend state (preferred) and chat messages (fallback)
                    const hasRunningAnalysis = chatMessages.some((m) =>
                      m.content?.includes('Running Analysis') || m.content?.includes('🔄 **Running Analysis')
                    );
                    const hasStep2 = chatMessages.some((m) =>
                      m.content?.includes('Step 2: Gathering Patient Information') || m.content?.includes('🔄 **Step 2:')
                    );
                    const hasStep3 = chatMessages.some((m) =>
                      m.content?.includes('Step 3: Analyzing Meal Plan Objective') || m.content?.includes('🔄 **Step 3:')
                    );
                    const hasStep4 = chatMessages.some((m) =>
                      m.content?.includes('Step 4: Verification') || m.content?.includes('🔄 **Step 4:')
                    );
                    const hasStep5 = chatMessages.some((m) =>
                      m.content?.includes('Step 5: Calculating Nutrition Requirements') || m.content?.includes('🔄 **Step 5:')
                    );
                    const hasVerificationMsg = chatMessages.some((m) =>
                      m.content?.includes('Verification Needed') || hasStep4
                    );
                    const hasApprovalSuccessMsg = chatMessages.some((m) =>
                      m.content?.includes('✅ Requirements approved') || m.content?.includes('meal plan created successfully')
                    );

                    const requirementsPresented =
                      Boolean(workflowState?.requirementsPresented) ||
                      chatMessages.some((m) => m.content?.includes('**Nutrition Requirements**') || m.content?.includes('Daily Nutrition Requirements'));
                    const requirementsApproved = Boolean(workflowState?.requirementsApproved) || hasApprovalSuccessMsg;

                    const verificationActive =
                      Boolean(workflowState?.pendingVerificationField) || hasVerificationMsg;

                    // Determine the single active step (avoid showing "analysis" when we're verifying/approving)
                    const backendStep = (workflowState?.currentStep || '').toLowerCase();
                    let currentStepId: string | null = null;

                    if (hasApprovalSuccessMsg) {
                      currentStepId = 'done';
                    } else if (requirementsApproved) {
                      // Approved but meal plan creation may still be running
                      currentStepId = 'done';
                    } else if (requirementsPresented) {
                      currentStepId = 'approve';
                    } else if (backendStep.includes('calculate') || hasStep5) {
                      currentStepId = 'step3';
                    } else if (backendStep.includes('verify') || verificationActive || hasStep4) {
                      currentStepId = 'verify';
                    } else if (backendStep.includes('analyze') || hasStep3) {
                      currentStepId = 'step2';
                    } else if (backendStep.includes('collect') || hasStep2) {
                      currentStepId = 'step1';
                    } else if (hasRunningAnalysis || requirementsChatSessionId) {
                      currentStepId = 'initial';
                    }

                    // Mark steps based on the active step + known completions
                    const idx = steps.findIndex((s) => s.id === currentStepId);
                    if (idx >= 0) {
                      // Complete all steps before the active one
                      for (let i = 0; i < idx; i++) steps[i].status = 'complete';
                      steps[idx].status = 'in_progress';
                    }

                    // Verification can be optional: if it happened and we're past it, mark it complete.
                    if (!verificationActive && hasVerificationMsg) {
                      const vIdx = steps.findIndex((s) => s.id === 'verify');
                      if (vIdx >= 0) steps[vIdx].status = 'complete';
                    }

                    // When requirements are presented, that means review is complete and we are waiting for approval
                    if (requirementsPresented) {
                      const reviewIdx = steps.findIndex((s) => s.id === 'review');
                      if (reviewIdx >= 0) steps[reviewIdx].status = 'complete';
                      const approveIdx = steps.findIndex((s) => s.id === 'approve');
                      if (approveIdx >= 0 && steps[approveIdx].status === 'pending') steps[approveIdx].status = 'in_progress';
                    }

                    // When approved, mark approval complete; "Done" is in progress until we see the success message
                    if (requirementsApproved) {
                      const approveIdx = steps.findIndex((s) => s.id === 'approve');
                      if (approveIdx >= 0) steps[approveIdx].status = 'complete';
                      const doneIdx = steps.findIndex((s) => s.id === 'done');
                      if (doneIdx >= 0) steps[doneIdx].status = hasApprovalSuccessMsg ? 'complete' : 'in_progress';
                    }

                    // Compute progress: complete=1, in_progress=0.5, pending=0
                    const progressUnits = steps.reduce((acc, s) => {
                      if (s.status === 'complete') return acc + 1;
                      if (s.status === 'in_progress') return acc + 0.5;
                      return acc;
                    }, 0);
                    const progressPercentage = Math.round((progressUnits / steps.length) * 100);

                    return (
                      <div className="requirements-chat-container" style={{ 
                        marginTop: '20px', 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        width: '100%',
                        minHeight: '300px'
                      }}>
                        {/* Progress Bar */}
                        <div style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '20px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}>
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '8px'
                            }}>
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Progress</h4>
                              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                                {Math.round(progressPercentage)}%
                              </span>
                            </div>
                            <div style={{
                              width: '100%',
                              height: '8px',
                              backgroundColor: '#e5e7eb',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${progressPercentage}%`,
                                height: '100%',
                                backgroundColor: '#2563eb',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                            {steps.map((step, index) => {
                              const isActive = step.status === 'in_progress';
                              const isComplete = step.status === 'complete';
                              
                              return (
                                <div
                                  key={step.id}
                                  style={{
                                    flex: '0 0 160px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    backgroundColor: isActive ? '#eff6ff' : isComplete ? '#f0fdf4' : 'transparent',
                                    transition: 'background-color 0.2s'
                                  }}
                                >
                                  <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isComplete ? '#10b981' : isActive ? '#2563eb' : '#e5e7eb',
                                    color: isComplete || isActive ? '#ffffff' : '#9ca3af',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    transition: 'all 0.2s'
                                  }}>
                                    {isComplete ? '✓' : index + 1}
                                  </div>
                                  <div style={{
                                    fontSize: '12px',
                                    textAlign: 'center',
                                    color: isActive ? '#2563eb' : isComplete ? '#059669' : '#9ca3af',
                                    fontWeight: isActive ? 600 : 400,
                                    lineHeight: '1.4'
                                  }}>
                                    {step.label}
                                  </div>
                                  {isActive && (
                                    <div style={{
                                      fontSize: '10px',
                                      color: '#2563eb',
                                      fontWeight: 500
                                    }}>
                                      In Progress
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div style={{ 
                          display: 'flex',
                          gap: '20px',
                          width: '100%'
                        }}>
                          {/* Chat Interface */}
                          <div style={{ flex: '1 1 60%', minWidth: 0 }}>
                            <div className="requirements-chat-header">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                <h3 style={{ margin: 0 }}>Review Nutrition Requirements</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {!(workflowState?.requirementsApproved || chatMessages.some((m: { role?: string; content?: unknown }) => m.role === 'assistant' && typeof m.content === 'string' && (m.content.includes('Requirements approved') || m.content.includes('meal plan created successfully')))) && (
                                    <button
                                      type="button"
                                      className="secondary-button"
                                      onClick={async () => {
                                        await clearRequirementsChatSession('cancel_workflow_clicked');
                                        setIsMealPlanEditMode(false);
                                      }}
                                      title="Cancel this workflow and release server resources"
                                    >
                                      Cancel workflow
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="secondary-button"
                                    onClick={async () => {
                                      await clearRequirementsChatSession('close_clicked');
                                      setIsMealPlanEditMode(false);
                                    }}
                                  >
                                    Close
                                  </button>
                                </div>
                              </div>
                              <p>Please review the calculated requirements and approve or request modifications.</p>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                Session ID: {requirementsChatSessionId || 'Connecting...'} | Messages: {chatMessages.length} | Loading: {mealPlanLoading ? 'Yes' : 'No'} | WS: {chatWsConnected ? 'Connected' : 'Disconnected'}
                              </p>
                            </div>
                        <div
                          ref={chatMessagesContainerRef}
                          className="requirements-chat-messages"
                          style={{ 
                            maxHeight: '400px', 
                            overflowY: 'auto', 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            padding: '16px',
                            backgroundColor: '#f9fafb',
                            marginBottom: '16px'
                          }}
                        >
                        {chatMessages.length === 0 ? (
                          <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                            Loading messages...
                          </div>
                        ) : (
                          chatMessages.map((message) => {
                            const isDoctor = message.role === 'user' || message.sender_type === 'doctor';
                            
                            // Parse options from message content
                            // Look for "**Recommended options:**" followed by comma-separated values
                            const optionsMatch = message.content.match(/\*\*Recommended options:\*\*\s*([^\n]+)/);
                            const optionsText = optionsMatch ? optionsMatch[1].trim() : null;
                            const options: string[] = optionsText ? optionsText.split(',').map((opt: string) => opt.trim()).filter((val: string) => Boolean(val)) : [];
                            
                            // Extract question text without options
                            const questionText = optionsMatch 
                              ? message.content.substring(0, optionsMatch.index).trim()
                              : message.content;
                            
                            return (
                              <div
                                key={message.id}
                                style={{
                                  marginBottom: '12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: isDoctor ? 'flex-end' : 'flex-start',
                                }}
                              >
                                <div
                                  style={{
                                    maxWidth: '70%',
                                    padding: '10px 14px',
                                    borderRadius: '12px',
                                    backgroundColor: isDoctor ? '#6366f1' : '#f3f4f6',
                                    color: isDoctor ? '#ffffff' : '#111827',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                    {questionText}
                                  </div>
                                  
                                  {/* Display options as clickable buttons */}
                                  {!isDoctor && options.length > 0 && (
                                    <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {options.map((option: string, idx: number) => {
                                        const isSelected = selectedOptions[message.id] === option;
                                        return (
                                          <button
                                            key={idx}
                                            type="button"
                                            onClick={async () => {
                                              // Mark as selected immediately for visual feedback
                                              setSelectedOptions(prev => ({ ...prev, [message.id]: option }));
                                              // Set input value and send automatically
                                              setChatMessageInput(option);
                                              await handleSendChatMessage();
                                            }}
                                            style={{
                                              padding: '6px 12px',
                                              backgroundColor: isSelected ? '#6366f1' : '#ffffff',
                                              color: isSelected ? '#ffffff' : '#111827',
                                              border: `1px solid ${isSelected ? '#6366f1' : '#d1d5db'}`,
                                              borderRadius: '6px',
                                              fontSize: '13px',
                                              cursor: sendingMessage ? 'not-allowed' : 'pointer',
                                              transition: 'all 0.2s ease',
                                              opacity: sendingMessage ? 0.6 : 1,
                                            }}
                                            onMouseEnter={(e) => {
                                              if (!sendingMessage && !isSelected) {
                                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                                e.currentTarget.style.borderColor = '#9ca3af';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = '#ffffff';
                                                e.currentTarget.style.borderColor = '#d1d5db';
                                              }
                                            }}
                                            disabled={sendingMessage}
                                          >
                                            {option}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {message.created_at && (
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        opacity: 0.7,
                                        marginTop: '4px',
                                      }}
                                    >
                                      {new Date(message.created_at).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                        </div>
                                  )}
                          </div>
                              </div>
                            );
                          })
                        )}
                        {/* Current question from state (restore after idle / when messages are stale) — hide if already shown as last message to avoid duplicate */}
                        {(() => {
                          const lastMsg = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null;
                          const lastIsAssistant = lastMsg && lastMsg.role === 'assistant';
                          const currentQ = (workflowState?.pendingGoalQuestion ?? '').trim();
                          const lastMatchesCurrent = lastIsAssistant && currentQ && typeof lastMsg?.content === 'string' && lastMsg.content.trim().startsWith(currentQ);
                          return Boolean(workflowState?.waitingForDoctorResponse && (workflowState.pendingGoalQuestion || workflowState.pendingGoalField) && !lastMatchesCurrent);
                        })() && (
                          <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <div style={{
                              maxWidth: '70%',
                              padding: '10px 14px',
                              borderRadius: '12px',
                              backgroundColor: '#f3f4f6',
                              color: '#111827',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}>
                              <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                {workflowState?.pendingGoalQuestion || `Answer: ${workflowState?.pendingGoalField}`}
                              </div>
                              {Array.isArray(workflowState?.pendingGoalOptions) && (workflowState?.pendingGoalOptions?.length ?? 0) > 0 && (
                                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {(workflowState?.pendingGoalOptions ?? []).map((opt: string | number, idx: number) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={async () => {
                                        await handleSendChatMessage(String(opt));
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#ffffff',
                                        color: '#111827',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        cursor: sendingMessage ? 'not-allowed' : 'pointer',
                                        opacity: sendingMessage ? 0.6 : 1,
                                      }}
                                      disabled={sendingMessage}
                                    >
                                      {String(opt)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="requirements-chat-input" style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          value={chatMessageInput}
                          onChange={(e) => setChatMessageInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && chatMessageInput.trim() && !sendingMessage) {
                              handleSendChatMessage();
                            }
                          }}
                          placeholder={requirementsChatSessionId ? "Type 'approve' or describe modifications..." : "Waiting for session..."}
                          style={{
                            flex: 1,
                            padding: '10px 14px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                          }}
                          disabled={sendingMessage || !requirementsChatSessionId}
                        />
                        <button
                          onClick={() => handleSendChatMessage()}
                          disabled={!chatMessageInput.trim() || sendingMessage || !requirementsChatSessionId}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6366f1',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: sendingMessage ? 'not-allowed' : 'pointer',
                            opacity: !chatMessageInput.trim() || sendingMessage ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          {sendingMessage ? (
                            <>
                              <span className="spinner" style={{
                                display: 'inline-block',
                                width: '14px',
                                height: '14px',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderTopColor: 'white',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite',
                              }}></span>
                              <span>Analyzing...</span>
                            </>
                          ) : (
                            'Send'
                          )}
                        </button>
                      </div>
                      </div>
                      
                      {/* Recommendations Table */}
                      {workflowState?.requirementsPresented && (workflowState.baseIcmrRecommendations || workflowState.adjustedRecommendations) && (
                        <div style={{ flex: '1 1 40%', minWidth: 0 }}>
                          <div style={{ 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            padding: '16px',
                            backgroundColor: '#ffffff',
                            maxHeight: '600px',
                            overflowY: 'auto'
                          }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                              Recommendations Comparison
                            </h4>
                            {(workflowState.baseIcmrRecommendations?.source_demographics || workflowState.adjustedRecommendations?.source_demographics) && (
                              <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '6px', fontSize: '0.85rem', color: '#6b7280' }}>
                                Based on: {(workflowState.baseIcmrRecommendations?.source_demographics?.gender || workflowState.adjustedRecommendations?.source_demographics?.gender) || 'N/A'}, 
                                Age: {(workflowState.baseIcmrRecommendations?.source_demographics?.age || workflowState.adjustedRecommendations?.source_demographics?.age) || 'N/A'} years, 
                                Weight: {(workflowState.baseIcmrRecommendations?.source_demographics?.weight_kg || workflowState.adjustedRecommendations?.source_demographics?.weight_kg) || 'N/A'} kg, 
                                Activity: {(workflowState.baseIcmrRecommendations?.source_demographics?.activity_level || workflowState.adjustedRecommendations?.source_demographics?.activity_level) || 'N/A'}
                    </div>
                  )}
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderRight: '1px solid #e5e7eb' }}>Food Group</th>
                                    <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#374151', borderRight: '1px solid #e5e7eb' }}>ICMR Base</th>
                                    <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Plan Adjusted</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    { key: 'cereals_millets_g', label: 'Cereals & Millets', unit: 'g' },
                                    { key: 'pulses_beans_g', label: 'Pulses & Beans', unit: 'g' },
                                    { key: 'glv_g', label: 'Green Leafy Vegetables', unit: 'g' },
                                    { key: 'vegetables_g', label: 'Vegetables', unit: 'g' },
                                    { key: 'roots_tubers_g', label: 'Roots & Tubers', unit: 'g' },
                                    { key: 'fruits_g', label: 'Fruits', unit: 'g' },
                                    { key: 'nuts_g', label: 'Nuts', unit: 'g' },
                                    { key: 'milk_curd_ml', label: 'Milk & Curd', unit: 'ml' },
                                    { key: 'fats_oils_g', label: 'Fats & Oils', unit: 'g' },
                                    { key: 'meat_poultry_g', label: 'Meat & Poultry', unit: 'g' },
                                    { key: 'fish_seafood_g', label: 'Fish & Seafood', unit: 'g' },
                                    { key: 'egg_g', label: 'Egg', unit: 'g' },
                                  ].map((fg, idx, arr) => {
                                    const baseValue = workflowState.baseIcmrRecommendations?.[fg.key] as number | undefined;
                                    const adjustedValue = workflowState.adjustedRecommendations?.[fg.key] as number | undefined;
                                    const change = baseValue && adjustedValue ? adjustedValue - baseValue : null;
                                    const changePercent = baseValue && adjustedValue && baseValue > 0 ? ((adjustedValue - baseValue) / baseValue * 100) : null;
                                    const isIncrease = change !== null && change > 0;
                                    const isDecrease = change !== null && change < 0;
                                    
                                    return (
                                      <tr key={fg.key} style={{ borderBottom: idx < arr.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                        <td style={{ padding: '8px', color: '#111827', borderRight: '1px solid #e5e7eb' }}>{fg.label}</td>
                                        <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                                          {baseValue !== undefined && baseValue !== null ? `${baseValue} ${fg.unit}` : '-'}
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                          {adjustedValue !== undefined && adjustedValue !== null ? (
                                            <div>
                                              <div style={{ fontWeight: '600', color: '#111827' }}>
                                                {adjustedValue} {fg.unit}
                                              </div>
                                              {change !== null && changePercent !== null && (
                                                <div style={{ 
                                                  fontSize: '0.75rem',
                                                  color: isIncrease ? '#059669' : isDecrease ? '#dc2626' : '#6b7280',
                                                  marginTop: '2px'
                                                }}>
                                                  {isIncrease ? '+' : ''}{change.toFixed(0)} ({isIncrease ? '+' : ''}{changePercent.toFixed(1)}%)
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <span style={{ color: '#9ca3af' }}>-</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            
                            {/* Nutrients Comparison Table */}
                            {workflowState.baseIcmrNutrients || workflowState.adjustedNutrients ? (
                              <div style={{ marginTop: '24px' }}>
                                <h5 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                                  Nutrients Comparison
                                </h5>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                    <thead>
                                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderRight: '1px solid #e5e7eb' }}>Nutrient</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#374151', borderRight: '1px solid #e5e7eb' }}>ICMR Base</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Plan Adjusted</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[
                                        // Macros
                                        { key: 'calories_kcal', label: 'Calories', unit: 'kcal' },
                                        { key: 'protein_g', label: 'Protein', unit: 'g' },
                                        { key: 'carbohydrates_g', label: 'Carbohydrates', unit: 'g' },
                                        { key: 'fat_g', label: 'Fat', unit: 'g' },
                                        { key: 'fiber_g', label: 'Fiber', unit: 'g' },
                                        // Vitamins
                                        { key: 'vitamin_a_mcg', label: 'Vitamin A', unit: 'mcg' },
                                        { key: 'vitamin_b1_mg', label: 'Vitamin B1 (Thiamine)', unit: 'mg' },
                                        { key: 'vitamin_b2_mg', label: 'Vitamin B2 (Riboflavin)', unit: 'mg' },
                                        { key: 'vitamin_b3_mg', label: 'Vitamin B3 (Niacin)', unit: 'mg' },
                                        { key: 'vitamin_b6_mg', label: 'Vitamin B6', unit: 'mg' },
                                        { key: 'vitamin_b9_folate_mcg', label: 'Folate (B9)', unit: 'mcg' },
                                        { key: 'vitamin_b12_mcg', label: 'Vitamin B12', unit: 'mcg' },
                                        { key: 'vitamin_c_mg', label: 'Vitamin C', unit: 'mg' },
                                        { key: 'vitamin_d_mcg', label: 'Vitamin D', unit: 'mcg' },
                                        { key: 'vitamin_e_mg', label: 'Vitamin E', unit: 'mg' },
                                        { key: 'vitamin_k_mcg', label: 'Vitamin K', unit: 'mcg' },
                                        // Minerals
                                        { key: 'calcium_mg', label: 'Calcium', unit: 'mg' },
                                        { key: 'magnesium_mg', label: 'Magnesium', unit: 'mg' },
                                        { key: 'iron_mg', label: 'Iron', unit: 'mg' },
                                        { key: 'zinc_mg', label: 'Zinc', unit: 'mg' },
                                        { key: 'phosphorus_mg', label: 'Phosphorus', unit: 'mg' },
                                        { key: 'potassium_mg', label: 'Potassium', unit: 'mg' },
                                        { key: 'sodium_mg', label: 'Sodium', unit: 'mg' },
                                        { key: 'copper_mg', label: 'Copper', unit: 'mg' },
                                        { key: 'manganese_mg', label: 'Manganese', unit: 'mg' },
                                        { key: 'selenium_mcg', label: 'Selenium', unit: 'mcg' },
                                      ].map((nutrient, idx) => {
                                        const baseValue = workflowState.baseIcmrNutrients?.[nutrient.key] as number | undefined;
                                        const adjustedValue = workflowState.adjustedNutrients?.[nutrient.key] as number | undefined;
                                        const change = baseValue !== undefined && baseValue !== null && adjustedValue !== undefined && adjustedValue !== null ? adjustedValue - baseValue : null;
                                        const changePercent = baseValue !== undefined && baseValue !== null && baseValue > 0 && adjustedValue !== undefined && adjustedValue !== null ? ((adjustedValue - baseValue) / baseValue * 100) : null;
                                        const isIncrease = change !== null && change > 0;
                                        const isDecrease = change !== null && change < 0;
                                        
                                        // Only show nutrients that have at least one value
                                        if ((baseValue === undefined || baseValue === null) && (adjustedValue === undefined || adjustedValue === null)) {
                                          return null;
                                        }
                                        
                                        return (
                                          <tr key={nutrient.key} style={{ borderBottom: idx < 29 ? '1px solid #e5e7eb' : 'none' }}>
                                            <td style={{ padding: '8px', color: '#111827', borderRight: '1px solid #e5e7eb' }}>{nutrient.label}</td>
                                            <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                                              {baseValue !== undefined && baseValue !== null ? `${parseFloat(baseValue.toFixed(2))} ${nutrient.unit}` : '-'}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                              {adjustedValue !== undefined && adjustedValue !== null ? (
                                                <div>
                                                  <div style={{ fontWeight: '600', color: '#111827' }}>
                                                    {parseFloat(adjustedValue.toFixed(2))} {nutrient.unit}
                                                  </div>
                                                  {change !== null && changePercent !== null && (
                                                    <div style={{ 
                                                      fontSize: '0.75rem',
                                                      color: isIncrease ? '#059669' : isDecrease ? '#dc2626' : '#6b7280',
                                                      marginTop: '2px'
                                                    }}>
                                                      {isIncrease ? '+' : ''}{parseFloat(change.toFixed(2))} {nutrient.unit} ({isIncrease ? '+' : ''}{changePercent.toFixed(1)}%)
                                                    </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <span style={{ color: '#9ca3af' }}>-</span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      }).filter(Boolean)}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

              <div className="patient-tab-panel" style={{ display: activeTab === 'nutritionProfile' ? 'block' : 'none' }}>
                {patientId ? <PatientNutritionProfileTab patientId={Number(patientId)} /> : null}
              </div>

              <div className="patient-tab-panel" style={{ display: (activeTab === 'metrics' || activeTab === 'scores') ? 'block' : 'none' }}>
                {patientId ? (
                  <HealthVitalsInsightsView
                    items={filteredDerivedInsights}
                    loading={derivedLoading || derivedMetricsLoading}
                    error={derivedError || derivedMetricsError}
                    insightsSubTab={insightsSubTab}
                    onInsightsSubTabChange={(tab) => {
                      setInsightsSubTab(tab);
                      setActiveTab(tab);
                    }}
                    editMode={insightsEditMode}
                    onEditModeChange={setInsightsEditMode}
                    edits={insightsEdits}
                    onEditsChange={setInsightsEdits}
                    onSave={handleInsightsSave}
                    onCancel={handleCancelInsightsEdit}
                    saving={insightsSaving}
                    saveButtonLabel={editableInsightsCount > 0 ? `Save ${editableInsightsCount} metric${editableInsightsCount === 1 ? '' : 's'}` : undefined}
                    message={null}
                    messageLevel="info"
                    showMessageBox={false}
                    showSourceColumn
                  />
                ) : null}
              </div>
            </section>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Create Appointment Modal */}
      {showCreateAppointmentModal && (
        <div
          className="patient-detail-modal-overlay"
          onClick={() => !createAppointmentSubmitting && setShowCreateAppointmentModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-appointment-title"
        >
          <div
            className="patient-detail-modal create-appointment-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="create-appointment-title" className="create-appointment-modal-title">
              Create Appointment
            </h2>
            {createAppointmentError && (
              <div className="create-appointment-error">{createAppointmentError}</div>
            )}
            <div className="create-appointment-form">
              <div className="create-appointment-practice-type-row">
                {createAppointmentPractices.length > 1 && (
                  <div className="form-group">
                    <label>Practice</label>
                    <select
                      value={createAppointmentSelectedPracticeId ?? ''}
                      onChange={(e) =>
                        setCreateAppointmentSelectedPracticeId(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      className="form-select create-appointment-practice-select"
                    >
                      {createAppointmentPractices.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.practice_name ?? `Practice ${p.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={createAppointmentType}
                    onChange={(e) => setCreateAppointmentType(e.target.value)}
                    className="form-select"
                  >
                    <option value="consultation">Consultation</option>
                    <option value="online_consultation">Online Consultation</option>
                    <option value="follow_up">Follow-up</option>
                    <option value="hospital_visit">Hospital Visit</option>
                  </select>
                </div>
              </div>
              <div className="create-appointment-side-by-side">
                <div className="create-appointment-calendar-column">
                  <div className="form-group create-appointment-calendar-group">
                    <label>Date</label>
                    <div className="create-appointment-calendar-wrapper">
                      <AvailabilityCalendar
                        selectedDate={
                          createAppointmentSelectedDate
                            ? new Date(createAppointmentSelectedDate + 'T12:00:00')
                            : new Date()
                        }
                        displayedMonth={createAppointmentDisplayedMonth}
                        leaveSlots={createAppointmentLeaveSlots}
                        appointmentCounts={createAppointmentCounts}
                        onDateSelect={(date) => setCreateAppointmentSelectedDate(createAppointmentFormatDateString(date))}
                        onMonthChange={(month) => setCreateAppointmentDisplayedMonth(month)}
                        hasFullDayLeave={createAppointmentHasFullDayLeave}
                        hasPartialLeave={createAppointmentHasPartialLeave}
                        isDateEnabled={createAppointmentIsDateEnabled}
                        isLoadingAppointmentCounts={createAppointmentCountsLoading}
                      />
                    </div>
                    {createAppointmentPracticeDayNames.size > 0 && (
                      <p className="create-appointment-calendar-hint">
                        Only days when you have practice timings are selectable.
                      </p>
                    )}
                  </div>
                </div>
                <div className="create-appointment-slots-column">
                  <div className="form-group create-appointment-slots-group">
                    <label>Available Slots</label>
                    <div className="create-appointment-slots-legend">
                      <span className="create-appointment-slots-legend-item">
                        <span className="create-appointment-slot-legend-dot booked" />
                        Booked
                      </span>
                      <span className="create-appointment-slots-legend-item">
                        <span className="create-appointment-slot-legend-dot selected" />
                        Selected
                      </span>
                    </div>
                    {createAppointmentSlotsLoading ? (
                      <div className="create-appointment-slots-loading">Loading slots...</div>
                    ) : createAppointmentSlots?.available_slots?.length ? (
                      <div className="create-appointment-slots-grid">
                        {createAppointmentSlots.available_slots.map((slot) => (
                          <button
                            key={slot.time_24h}
                            type="button"
                            className={`slot-btn ${slot.is_booked ? 'booked' : ''} ${createAppointmentSelectedSlot?.time_24h === slot.time_24h ? 'selected' : ''}`}
                            onClick={() => !slot.is_booked && setCreateAppointmentSelectedSlot({ time: slot.time, time_24h: slot.time_24h })}
                            disabled={!!slot.is_booked}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="create-appointment-no-slots">
                        {createAppointmentSlots?.message || 'No slots available for this date. Try another date.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="create-appointment-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowCreateAppointmentModal(false)}
                disabled={createAppointmentSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => submitCreateAppointment()}
                disabled={
                  createAppointmentSubmitting ||
                  !createAppointmentSelectedSlot ||
                  createAppointmentSlotsLoading
                }
              >
                {createAppointmentSubmitting ? 'Creating...' : 'Create Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PatientDetailPage;
